/**
 * tasks/{id} — Phase 2B Tasks core.
 *
 * Schema:
 *   title              short label, required, displayed everywhere
 *   description        optional free-text body
 *   assigneeUids[]     1+ uids — who has to do it
 *   dueAt              Timestamp or null (open-ended)
 *   completedBy        uid -> Timestamp (per-assignee completion)
 *   createdAt / createdBy / updatedAt
 *
 * Completion model:
 *   Mirrors the ack-required pattern from messaging — each assignee
 *   completes their own slot in completedBy{}. Task is "done" when
 *   every assignee uid is a key in completedBy. Director can also
 *   force-complete on someone's behalf (rule allows Director to touch
 *   any field), but the default UI sends per-assignee completion.
 *
 * Rules: Director read/write all; active staff read all (so the
 * roster can see what's outstanding); assignee updates only their own
 * completedBy slot.
 *
 * Recurring tasks (PR 2 of this phase) will add a `recurrence` field
 * and a Function that fans out instances. Out of scope here.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  collectionGroup,
  deleteDoc,
  deleteField,
  doc,
  getDocFromCache,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp as FirestoreTimestamp,
  updateDoc,
  writeBatch,
  where,
  type DocumentData,
  type FieldValue,
  type QueryDocumentSnapshot,
  type Timestamp,
} from 'firebase/firestore';
import {
  deleteObject,
  ref as storageRef,
} from 'firebase/storage';
import type { User } from 'firebase/auth';
import { recitalCompTrackerCopy } from './trackerCopy';
import { downscaleImage } from './offerings';
// The upload code loads on the first upload, not with the first screen
// (speed plan 2026-10-19, P2: it was on the startup path for every visitor).
const loadAttachmentGateway = () => import('./attachmentUploadGateway');
import { db, storage } from './firebase';
import { useAuth } from './auth-context';
import { functionHeaders } from './functionSecurity';
import { useReadEpoch } from './readEpoch';
import { completeJrStaffAliasTask, loadJrStaffAliasTaskResponses, loadJrStaffAliasTasks } from './jrStaffReadsClient';
import {
  clearNotificationsForTask,
  notifyUsers,
  type NotifKind,
} from './notifications';
import { isStaffRole, useUserDoc, type UserDoc, type UserRole } from './users';
import { teacherDisplayName } from './teacherTitle.ts';
import { bagItemTaskGroupId } from './bagItemTask.ts';
import { taskAssignedToViewer } from './roomRequests';
import {
  isDateKey,
  isStudioVoiceTask,
  isTaskWaitingToShow,
  normalizeReminderDays,
  TASK_STUDIO_NAME,
} from './taskSchedule';

// Re-exported because every task screen already imports from lib/tasks.
export { isTaskWaitingToShow } from './taskSchedule';

export type Recurrence = 'none' | 'daily' | 'weekly' | 'monthly';

export const RECURRENCE_LABELS: Record<Recurrence, string> = {
  none: 'One-time',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

/**
 * Task "kind" controls what the assignee actually has to deliver and
 * what UI we show inside the row. Start with two; add as patterns
 * stabilize.
 *
 *   general       — just mark done when you've done the thing.
 *   upload-music  — paste a Google Drive link to the audio file. The
 *                   link doubles as completion (posting it = your slot
 *                   is done). Assigner sees the link inline on the row.
 *   costume-pickup — family marks done after the costume has been picked up.
 *   timed-reminder — assignee sees a dated reminder and marks it done after reading.
 *
 * When Drive OAuth ships, "upload-music" rows will swap their pasted-
 * link input for a native Drive picker — data shape unchanged.
 */
export type TaskKind =
  | 'general'
  | 'upload-music'
  | 'upload-file'
  | 'availability-poll'
  | 'choice-poll'
  | 'payment-needed'
  | 'costume-pickup'
  | 'timed-reminder';

export const TASK_KIND_LABELS: Record<TaskKind, string> = {
  general: 'Other',
  'upload-music': 'Upload music',
  'upload-file': 'Upload file or link',
  'availability-poll': 'Availability check',
  'choice-poll': 'Poll or form',
  'payment-needed': 'Payment needed',
  'costume-pickup': 'Costume pickup',
  'timed-reminder': 'Timed reminder',
};

export type TaskResponseKind =
  | 'general'
  | 'upload-music'
  | 'upload-file'
  | 'availability-poll'
  | 'choice-poll';

/**
 * A concrete time an availability-poll task is asking about. Instead of
 * typing free text, assignees TAP the options that work for them (their
 * picks land in the response doc's selectedOptionIds). date/start/end are
 * plain strings ('YYYY-MM-DD' / 'HH:MM') rather than a Timestamp so the
 * composer can build them with simple date/time inputs, matching the
 * 30-minute-friendly pattern /calendar/new already uses.
 */
export interface TaskTimeOption {
  id: string;
  date: string;
  start: string;
  end: string;
}

/** One plain-language answer in a single-choice poll or form. */
export interface TaskChoiceOption {
  id: string;
  label: string;
}

export const MAX_TASK_CHOICE_OPTIONS = 10;

/**
 * Something the person who WROTE the task attached to it: a photo of a
 * schedule, a shot of a costume rail, a link to a doc. Not to be confused
 * with TaskUploadLink, which is what an ASSIGNEE hands back.
 *
 * Everyone who can see the task can see these, so nothing private belongs
 * here. `kind` only decides whether it renders as a thumbnail or a link row;
 * it is not a security boundary, and the Storage rules are what actually
 * decide who may upload.
 */
export interface TaskAttachment {
  /** Canonical gateway id for uploaded attachments. */
  id?: string;
  url: string;
  kind: 'image' | 'video' | 'file' | 'link';
  label: string;
  /** Finalized gateway reservation. Present only while an uploaded file is
   * waiting for the server-owned task create transaction to consume it. */
  reservationId?: string;
  /** Present for uploaded attachments. Links intentionally omit it. */
  contentType?: string;
  /** Original uploaded file name for the shared safe renderer. */
  name?: string;
  /** Verified gateway byte count. */
  size?: number;
  /** Lets an author remove the exact object without trusting its download URL. */
  storagePath?: string;
}

/**
 * Small on purpose. Every attachment is validated item by item in the
 * Firestore rules, and that budget is shared with timeOptions on the same
 * write - see the long note on hasTaskTimeOptionsShape. Four covers "here are
 * a couple of photos of the schedule" without crowding a poll off the same
 * task.
 */
export const MAX_TASK_ATTACHMENTS = 4;

/** Keeps a pasted link from being a javascript: or data: URL. */
export function isSafeAttachmentUrl(raw: string): boolean {
  const value = raw.trim();
  return /^https:\/\/\S+$/i.test(value) && value.length <= 2048;
}

export interface TaskUploadLink {
  url: string;
  label: string;
  note?: string;
  /**
   * Which of the parent task's timeOptions this assignee tapped. Only
   * meaningful for availability-poll responses; optional everywhere else.
   */
  selectedOptionIds?: string[];
  attachments?: TaskResponseAttachment[];
  savedAt: Timestamp | FieldValue;
}

export interface TaskResponseAttachment {
  id: string;
  kind: 'image' | 'video' | 'file';
  url: string;
  name: string;
  contentType: string;
  storagePath: string;
}

/**
 * The shared picker intentionally models storagePath as optional because
 * legacy thread attachments may not have one. Task responses are stricter:
 * Firestore rules bind every descriptor to a finalized gateway reservation
 * under this exact task. Validate that boundary before any response write.
 */
export function taskResponseAttachmentsForTask(
  taskId: string,
  value: readonly {
    id: string;
    kind: 'image' | 'video' | 'file';
    url: string;
    name: string;
    contentType: string;
    storagePath?: string;
  }[]
): TaskResponseAttachment[] {
  if (value.length > MAX_TASK_ATTACHMENTS) {
    throw new Error(`You can attach up to ${MAX_TASK_ATTACHMENTS} photos, videos, or files.`);
  }
  const expectedPrefix = `task-response-attachments/${taskId}/`;
  return value.map((attachment) => {
    if (
      !attachment.id.trim()
      || !isSafeAttachmentUrl(attachment.url)
      || !attachment.name.trim()
      || !attachment.contentType.trim()
      || !attachment.storagePath?.startsWith(expectedPrefix)
    ) {
      throw new Error('One response attachment was not finalized for this to-do. Remove it and try again.');
    }
    return {
      id: attachment.id,
      kind: attachment.kind,
      url: attachment.url,
      name: attachment.name.slice(0, 160),
      contentType: attachment.contentType.slice(0, 120),
      storagePath: attachment.storagePath,
    };
  });
}

export interface TaskResponseDoc extends TaskUploadLink {
  id: string;
  taskId: string;
  uid: string;
  kind: TaskResponseKind;
  updatedAt?: Timestamp | FieldValue;
}

/**
 * The one durable outcome claimed for a room-request task.
 *
 * This is written only after the task exists. A booking writes its claim in
 * the SAME Firestore transaction as the reservation and slot locks, while a
 * decline/close writes it in its own transaction before notifying. That one
 * shared document is what prevents two browser surfaces from resolving the
 * same request in contradictory ways.
 */
interface RoomRequestResolutionBase {
  claimedAt: Timestamp | FieldValue;
  /** One caller at a time may send the resolution notification. */
  notificationAttemptId?: string | null;
  notificationAttemptAt?: Timestamp | FieldValue | null;
}

export type RoomRequestResolution = RoomRequestResolutionBase &
  (
    | {
      action: 'book';
      reservationId: string;
      roomId: string;
      /** Room name read inside the booking transaction. */
      roomName?: string;
      /** Exact booked window, used if the first notice needs recovery. */
      startAt?: Timestamp | FieldValue;
      endAt?: Timestamp | FieldValue;
    }
    | {
      action: 'decline';
      reason: string;
    }
    | {
      action: 'close';
      /** Present when a previously approved calendar booking was removed. */
      removedBooking?: {
        reservationId: string;
        roomId: string;
        roomName?: string;
        startAt?: Timestamp | FieldValue;
        endAt?: Timestamp | FieldValue;
      };
    }
  );

export interface TaskDoc {
  id: string;
  title: string;
  description: string;
  assigneeUids: string[];
  /**
   * Optional links to specific students this task is "about" —
   * Bobbie sets this when she's assigning a homework-style task
   * ("Practice your solo before Friday"). The composer
   * auto-propagates each student's familyUids[] into assigneeUids so
   * the parent's account actually sees the task in /tasks. We keep
   * studentIds in addition to assigneeUids so the UI can render
   * "For: Ava B, Leo S" subtitles and a future Family Portal can
   * group tasks per dancer.
   */
  studentIds: string[];
  dueAt: Timestamp | null;
  completedBy: Record<string, Timestamp | FieldValue>;
  /** Present only on room requests once one resolution path has claimed it. */
  roomRequestResolution?: RoomRequestResolution;
  /**
   * Per-assignee "remind me later". uid -> the moment the task should start
   * nagging again. Keyed like completedBy so one person snoozing never hides
   * the task from anyone else. A task snoozed into the future drops out of
   * open lists and attention counts for that person only, and comes back on
   * its own once the time passes. Absent on tasks nobody has snoozed.
   */
  snoozedUntil?: Record<string, Timestamp | FieldValue>;
  /**
   * Cadence the task should repeat at. 'none' = one-shot. The next
   * instance is spawned automatically by the completer's client when
   * the final assignee marks it done; we use nextInstanceId to dedup.
   * Recurrence requires a dueAt — without one we have no anchor to
   * step from.
   */
  recurrence: Recurrence;
  /**
   * The id of the freshly-spawned successor task, set once the
   * recurring task fully completes. Null otherwise. Stops the
   * completion handler from re-cloning on subsequent (idempotent)
   * writes to the now-completed task.
   */
  nextInstanceId: string | null;
  /**
   * Kind of deliverable. Optional in the type for back-compat with
   * existing docs that pre-date this field — read sites treat missing
   * as 'general'. New writes always include it.
   */
  kind?: TaskKind;
  /**
   * Per-assignee deliverables or response notes. Keyed by uid like completedBy,
   * each assignee owns their own slot.
   */
  uploadLinks?: Record<string, TaskUploadLink>;
  /**
   * Photos and links the task's author attached when they wrote it, so
   * "review this schedule" can actually carry the schedule. Absent on tasks
   * written before this existed; read sites treat missing as none.
   */
  attachments?: TaskAttachment[];
  /**
   * Concrete time choices for an availability-poll task. Optional - a poll
   * without options falls back to the original free-text-only flow.
   * Capped at 6 to stay within the rules evaluator budget - unlike the
   * bulletin/classPost importantDates pattern this cap mirrors, each entry
   * here is a small map validated with its own regex, which is measurably
   * more expensive per item, so a smaller cap was needed to keep a single
   * task write comfortably under the ~1000-expression ceiling.
   */
  timeOptions?: TaskTimeOption[];
  /** Single-choice answers for a poll/form, such as T-shirt sizes. */
  choiceOptions?: TaskChoiceOption[];
  source?:
    | {
        type: 'message';
        threadId: string;
        messageId: string;
        preview: string;
      }
    | {
        type: 'discussion';
        discussionId: string;
        preview: string;
      }
    | {
        type: 'classPost';
        postId: string;
        offeringId: string;
        preview: string;
      }
    | {
        type: 'bulletin';
        bulletinId: string;
        preview: string;
      }
    | null;
  /**
   * PACKET CX-2: the studio day ('YYYY-MM-DD', America/Chicago) a waiting
   * to-do shows on. Present only while it waits; the morning sweep
   * (netlify/functions/lib/taskSchedule.mjs) removes it once it has shown and
   * alerted, and stamps shownOn. Server-only, like every field below.
   */
  showOn?: string | null;
  /** The studio day it showed on, once it has. */
  shownOn?: string | null;
  /**
   * Its people while it waits. assigneeUids is EMPTY until the Show on
   * morning, so no assignee read in the Hub can reach it before then. See
   * isTaskWaitingToShow.
   */
  showOnAssigneeUids?: string[];
  /** Reminders picked: days before the due date, largest first (90/60/30/15/7). */
  reminderDaysBefore?: number[];
  /** The studio due day those reminders count back from. */
  reminderDueOn?: string | null;
  /** days -> the studio day that reminder went out. */
  remindersSent?: Record<string, string>;
  createdAt: Timestamp | FieldValue;
  createdBy: string;
  /**
   * Denormalized display name of the creator, captured at creation time
   * (mirrors the classPosts.authorName pattern). Family accounts can't read
   * other users' profile docs under Firestore rules, so without this the
   * "Posted by ..." line would have no name to show for a staff-created
   * task. Optional/nullable for docs written before this field existed.
   */
  createdByName?: string | null;
  /**
   * Set only on the attendance nag to-do (id `attendanceNag_{offeringId}_{date}`,
   * written by netlify/functions/attendanceNag.mjs): the class and the day
   * the nag is about, so a task row can open Attendance on the right day.
   * No client may write a nag (the rules own that id namespace), so these
   * are read-only here. See lib/attendanceNagTask.ts.
   */
  attendanceNagOfferingId?: string | null;
  attendanceNagDate?: string | null;
  /**
   * Set only on a comp music to-do (id `compMusic_...`, written by
   * netlify/functions/lib/compMusicTasks.mjs): the comp dance whose music it
   * asks for, so the row can open that class's Music & Video tab and check
   * itself off once music is posted. Server-owned; no client writes it. See
   * lib/compMusicTask.ts.
   */
  compMusicOfferingId?: string | null;
  /**
   * Set only on a bag item to-do (id `bagItem_{groupId.length}_{groupId}_{itemId}`, written by
   * the item lane, netlify/functions/lib/bagGroupItemTodo.mjs): the group and
   * the item it is about, so the row can open the group. Server-owned. See
   * lib/bagItemTask.ts.
   */
  bagGroupId?: string | null;
  bagItemId?: string | null;
  /**
   * Set only on a costume pickup to-do (id
   * `costumePickup_{costumeId.length}_{costumeId}_{studentId}`, written by
   * netlify/functions/lib/costumePickupTodo.mjs when Kim marks a costume ready
   * for pickup): the costume and the dancer, so the row can open the label's
   * pickup page. Server-owned. See lib/costumePickupTask.ts.
   */
  costumeId?: string | null;
  costumeStudentId?: string | null;
  /**
   * A parent's to-do on her own family (N20, lib/familyTodoWrite.mjs). Only
   * that server lane writes it. Staff studio-wide lists leave these out
   * unless the viewer is on it or made it (isStudioWideHiddenTask).
   */
  familyTodo?: true;
  updatedAt: Timestamp | FieldValue;
}

// The nag to-do's client-side helpers live in a Firebase-free file so a plain
// node test can import them; re-exported here because every task screen
// already imports from lib/tasks and the check belongs beside isTaskDone.
export {
  ATTENDANCE_NAG_OPEN_LABEL,
  ATTENDANCE_NAG_REFUSED,
  ATTENDANCE_NAG_SELF_CLEARS,
  attendanceNagHref,
  isAttendanceNagTaskId,
} from './attendanceNagTask';

// The monthly award to-do's helpers, the same way (lib/studentAwardTask.ts,
// DECISIONS 170): never marked done by hand, one door, one line.
export {
  AWARD_TODO_OPEN_LABEL,
  awardTodoSelfClears,
  isStudentAwardTask,
} from './studentAwardTask';

function cleanString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

// Retained as an exported alias because existing probes and callers use this
// name. The implementation is shared with notification display copy.
export { recitalCompTrackerCopy as recitalCompDueDatesCopy };

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

function normalizeCompletedBy(value: unknown): Record<string, Timestamp | FieldValue> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, Timestamp | FieldValue>;
}

function normalizeRoomRequestResolution(value: unknown): RoomRequestResolution | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const claimedAt = raw.claimedAt as Timestamp | FieldValue;
  const attempt = {
    notificationAttemptId: cleanString(raw.notificationAttemptId),
    notificationAttemptAt: (raw.notificationAttemptAt ?? null) as Timestamp | FieldValue | null,
  };
  if (raw.action === 'book') {
    const reservationId = cleanString(raw.reservationId);
    const roomId = cleanString(raw.roomId);
    const roomName = cleanString(raw.roomName) ?? undefined;
    const startAt = raw.startAt as Timestamp | FieldValue | undefined;
    const endAt = raw.endAt as Timestamp | FieldValue | undefined;
    return reservationId && roomId
      ? { action: 'book', reservationId, roomId, roomName, startAt, endAt, claimedAt, ...attempt }
      : undefined;
  }
  if (raw.action === 'decline') {
    const reason = cleanString(raw.reason);
    return reason ? { action: 'decline', reason, claimedAt, ...attempt } : undefined;
  }
  if (raw.action !== 'close') return undefined;
  const removedRaw = raw.removedBooking && typeof raw.removedBooking === 'object'
    ? (raw.removedBooking as Record<string, unknown>)
    : null;
  const removedReservationId = cleanString(removedRaw?.reservationId);
  const removedRoomId = cleanString(removedRaw?.roomId);
  const removedBooking = removedReservationId && removedRoomId
    ? {
        reservationId: removedReservationId,
        roomId: removedRoomId,
        roomName: cleanString(removedRaw?.roomName) || undefined,
        startAt: removedRaw?.startAt as Timestamp | FieldValue | undefined,
        endAt: removedRaw?.endAt as Timestamp | FieldValue | undefined,
      }
    : undefined;
  return { action: 'close', claimedAt, removedBooking, ...attempt };
}

function normalizeRecurrence(value: unknown): Recurrence {
  return value === 'daily' || value === 'weekly' || value === 'monthly' ? value : 'none';
}

function normalizeTaskKind(value: unknown): TaskKind {
  return value === 'upload-music' ||
    value === 'upload-file' ||
    value === 'availability-poll' ||
    value === 'choice-poll' ||
    value === 'payment-needed' ||
    value === 'costume-pickup' ||
    value === 'timed-reminder'
    ? value
    : 'general';
}

function normalizeUploadLinks(value: unknown): Record<string, TaskUploadLink> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, TaskUploadLink>;
}

function normalizeTaskTimeOption(value: unknown): TaskTimeOption | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const id = cleanString(raw.id);
  const date = cleanString(raw.date);
  const start = cleanString(raw.start);
  const end = cleanString(raw.end);
  if (!id || !date || !start || !end) return null;
  return { id, date, start, end };
}

function normalizeTaskChoiceOption(value: unknown): TaskChoiceOption | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const id = cleanString(raw.id)?.slice(0, 40);
  const label = cleanString(raw.label)?.slice(0, 120);
  return id && label ? { id, label } : null;
}

// Capped at 6, not 12: the Firestore rules budget for validating a per-item
// map shape + regex against every timeOptions entry on a single write stops
// fitting well before 12 (see hasTaskTimeOptionsShape in firestore.rules).
// selectedOptionIds (below) is a separate, much cheaper check and keeps its
// higher cap.
export const MAX_TASK_TIME_OPTIONS = 6;

function normalizeTaskTimeOptions(value: unknown): TaskTimeOption[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(normalizeTaskTimeOption)
    .filter((opt): opt is TaskTimeOption => opt !== null)
    .slice(0, MAX_TASK_TIME_OPTIONS);
}

function normalizeTaskChoiceOptions(value: unknown): TaskChoiceOption[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(normalizeTaskChoiceOption)
    .filter((opt): opt is TaskChoiceOption => opt !== null)
    .slice(0, MAX_TASK_CHOICE_OPTIONS);
}

function normalizeSelectedOptionIds(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const ids = value.filter((item): item is string => typeof item === 'string').slice(0, 12);
  return ids.length > 0 ? ids : undefined;
}

function normalizeTaskResponseAttachments(value: unknown): TaskResponseAttachment[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const attachments = value.flatMap((item): TaskResponseAttachment[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    if (Object.keys(raw).some((key) => !['id', 'kind', 'url', 'name', 'contentType', 'storagePath'].includes(key))) return [];
    const id = cleanString(raw.id);
    const url = cleanString(raw.url);
    const name = cleanString(raw.name);
    const contentType = cleanString(raw.contentType);
    const storagePath = cleanString(raw.storagePath);
    const kind = raw.kind;
    if (!id || !url || !name || !contentType || !storagePath || !isSafeAttachmentUrl(url)) return [];
    if (kind !== 'image' && kind !== 'video' && kind !== 'file') return [];
    return [{ id, kind, url, name, contentType, storagePath }];
  }).slice(0, MAX_TASK_ATTACHMENTS);
  return attachments.length > 0 ? attachments : undefined;
}

export function normalizeTaskAttachments(value: unknown): TaskAttachment[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const attachments = value.flatMap((item): TaskAttachment[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    if (Object.keys(raw).some((key) => ![
      'id', 'url', 'kind', 'label', 'name', 'contentType', 'storagePath',
      'size',
    ].includes(key))) return [];
    const id = cleanString(raw.id);
    const url = cleanString(raw.url);
    const label = cleanString(raw.label);
    const name = cleanString(raw.name);
    const contentType = cleanString(raw.contentType);
    const storagePath = cleanString(raw.storagePath);
    const size = typeof raw.size === 'number' && Number.isFinite(raw.size) && raw.size >= 0
      ? raw.size
      : undefined;
    const kind = raw.kind;
    if (!url || !label || !isSafeAttachmentUrl(url)) return [];
    if (kind !== 'image' && kind !== 'video' && kind !== 'file' && kind !== 'link') return [];
    const hasAnyGatewayField = Boolean(id || name || contentType || storagePath || size != null);
    const hasFullGatewayShape = Boolean(id && name && contentType && storagePath);
    // Keep legacy compact task descriptors readable as download links. A
    // partially populated new descriptor is rejected; server-created rows
    // must carry the complete safe-renderer shape.
    if (kind !== 'link' && hasAnyGatewayField && !hasFullGatewayShape) return [];
    return [{
      ...(id ? { id } : {}),
      url,
      kind,
      label: label.slice(0, 120),
      ...(name ? { name } : {}),
      ...(contentType ? { contentType } : {}),
      ...(storagePath ? { storagePath } : {}),
      ...(size != null ? { size } : {}),
    }];
  }).slice(0, MAX_TASK_ATTACHMENTS);
  return attachments.length > 0 ? attachments : undefined;
}

/** Unique id for a new time-option row in the composer. */
export function newTaskTimeOptionId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const newTaskChoiceOptionId = newTaskTimeOptionId;

function formatTimeOfDay(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const period = h >= 12 ? 'pm' : 'am';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour12}${period}` : `${hour12}:${String(m).padStart(2, '0')}${period}`;
}

/** "Tue Jul 21, 5pm to 6pm" - shared by the family chip picker, the saved-
 * selections summary, and the staff tally so every surface reads the same
 * time the same way. */
export function formatTaskTimeOption(opt: TaskTimeOption): string {
  const parsed = new Date(`${opt.date}T00:00:00`);
  const dateLabel = Number.isNaN(parsed.getTime())
    ? opt.date
    : parsed.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  return `${dateLabel}, ${formatTimeOfDay(opt.start)} to ${formatTimeOfDay(opt.end)}`;
}

function normalizeTaskSource(value: unknown): TaskDoc['source'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const preview = cleanString(raw.preview) ?? '';
  if (raw.type === 'message') {
    const threadId = cleanString(raw.threadId);
    const messageId = cleanString(raw.messageId);
    return threadId && messageId ? { type: 'message', threadId, messageId, preview } : null;
  }
  if (raw.type === 'discussion') {
    const discussionId = cleanString(raw.discussionId);
    return discussionId ? { type: 'discussion', discussionId, preview } : null;
  }
  if (raw.type === 'classPost') {
    const postId = cleanString(raw.postId);
    const offeringId = cleanString(raw.offeringId);
    return postId && offeringId ? { type: 'classPost', postId, offeringId, preview } : null;
  }
  if (raw.type === 'bulletin') {
    const bulletinId = cleanString(raw.bulletinId);
    return bulletinId ? { type: 'bulletin', bulletinId, preview } : null;
  }
  return null;
}

function normalizeRemindersSent(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [key, stamp] of Object.entries(value as Record<string, unknown>)) {
    if (typeof stamp === 'string' && stamp) out[key] = stamp;
  }
  return out;
}

export function normalizeTaskDoc(id: string, value: unknown): TaskDoc {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return {
    id,
    title: recitalCompTrackerCopy(cleanString(raw.title) ?? 'Untitled to-do'),
    description: recitalCompTrackerCopy(cleanString(raw.description) ?? ''),
    assigneeUids: normalizeStringArray(raw.assigneeUids),
    studentIds: normalizeStringArray(raw.studentIds),
    dueAt: (raw.dueAt ?? null) as Timestamp | null,
    completedBy: normalizeCompletedBy(raw.completedBy),
    roomRequestResolution: normalizeRoomRequestResolution(raw.roomRequestResolution),
    snoozedUntil: normalizeCompletedBy(raw.snoozedUntil),
    recurrence: normalizeRecurrence(raw.recurrence),
    nextInstanceId: cleanString(raw.nextInstanceId),
    kind: normalizeTaskKind(raw.kind),
    uploadLinks: normalizeUploadLinks(raw.uploadLinks),
    attachments: normalizeTaskAttachments(raw.attachments),
    timeOptions: normalizeTaskTimeOptions(raw.timeOptions),
    choiceOptions: normalizeTaskChoiceOptions(raw.choiceOptions),
    source: normalizeTaskSource(raw.source),
    showOn: isDateKey(raw.showOn) ? raw.showOn : null,
    shownOn: isDateKey(raw.shownOn) ? raw.shownOn : null,
    showOnAssigneeUids: normalizeStringArray(raw.showOnAssigneeUids),
    reminderDaysBefore: normalizeReminderDays(raw.reminderDaysBefore),
    reminderDueOn: isDateKey(raw.reminderDueOn) ? raw.reminderDueOn : null,
    remindersSent: normalizeRemindersSent(raw.remindersSent),
    createdAt: raw.createdAt as Timestamp | FieldValue,
    createdBy: cleanString(raw.createdBy) ?? '',
    createdByName: cleanString(raw.createdByName),
    attendanceNagOfferingId: cleanString(raw.attendanceNagOfferingId),
    attendanceNagDate: cleanString(raw.attendanceNagDate),
    compMusicOfferingId: cleanString(raw.compMusicOfferingId),
    bagGroupId: cleanString(raw.bagGroupId),
    bagItemId: cleanString(raw.bagItemId),
    costumeId: cleanString(raw.costumeId),
    costumeStudentId: cleanString(raw.costumeStudentId),
    ...(raw.familyTodo === true ? { familyTodo: true } : {}),
    updatedAt: raw.updatedAt as Timestamp | FieldValue,
  };
}
/**
 * Step a JS Date by one period of the given recurrence. Monthly
 * handles end-of-month clamp (the 31st of one month rolls to the
 * 28th-30th of the next as appropriate).
 */
export function computeNextDueAt(prev: Date, recurrence: Recurrence): Date {
  if (recurrence === 'daily') {
    const d = new Date(prev);
    d.setDate(d.getDate() + 1);
    return d;
  }
  if (recurrence === 'weekly') {
    const d = new Date(prev);
    d.setDate(d.getDate() + 7);
    return d;
  }
  if (recurrence === 'monthly') {
    const d = new Date(prev);
    const targetMonth = d.getMonth() + 1;
    const targetYear = d.getFullYear() + (targetMonth > 11 ? 1 : 0);
    const month = targetMonth % 12;
    // Probe the last day of the target month to clamp the date safely.
    const lastDay = new Date(targetYear, month + 1, 0).getDate();
    d.setFullYear(targetYear, month, Math.min(prev.getDate(), lastDay));
    return d;
  }
  return prev;
}

export function isTaskDone(t: TaskDoc): boolean {
  const assigneeUids = Array.isArray(t.assigneeUids) ? t.assigneeUids : [];
  const completedBy = t.completedBy && typeof t.completedBy === 'object' ? t.completedBy : {};
  if (assigneeUids.length === 0) return false;
  return assigneeUids.every((uid) => uid in completedBy);
}

/** How long each "Remind me later" choice pushes a task out. */
export const SNOOZE_CHOICES = [
  { key: 'evening', label: 'Tonight', hours: 5 },
  { key: 'tomorrow', label: 'Tomorrow', hours: 24 },
  { key: 'week', label: 'Next week', hours: 24 * 7 },
] as const;

export type SnoozeChoiceKey = (typeof SNOOZE_CHOICES)[number]['key'];

/**
 * When this person asked to be reminded again, or null if they have not
 * snoozed it. Tolerates the field being absent on older task docs.
 */
export function taskSnoozedUntil(t: TaskDoc, uid: string | undefined): Date | null {
  if (!uid) return null;
  const raw = t.snoozedUntil?.[uid] as Timestamp | undefined;
  const asDate = raw && typeof (raw as Timestamp).toDate === 'function' ? (raw as Timestamp).toDate() : null;
  return asDate ?? null;
}

/** True while a task is snoozed into the future for this person. */
export function isTaskSnoozedForMe(t: TaskDoc, uid: string | undefined, now: Date = new Date()): boolean {
  const until = taskSnoozedUntil(t, uid);
  return !!until && until.getTime() > now.getTime();
}

/**
 * Is MY OWN slot on this task filled? Deliberately unaffected by snoozing -
 * "remind me later" quiets a task without marking it done, and the /tasks
 * page keeps a snoozed card visible in the open section (with its own
 * "Reminding you..." row) rather than sinking it into Past, which is where
 * this flag being true would send it. Kept name-distinct from `isTaskDone`
 * (which asks whether EVERY assignee finished, not just me).
 */
export function isTaskDoneForMe(
  t: TaskDoc,
  /**
   * One uid, or every sign-in one human holds. See taskAssignedToViewer in
   * lib/roomRequests.ts for why the widening lives on the read side: a Jr
   * Staff ticks a to-do on whichever account she is signed in to, and it is
   * finished for her on both.
   */
  uid: string | null | undefined | readonly string[]
): boolean {
  if (!uid) return false;
  const uids = typeof uid === 'string' ? [uid] : uid;
  return uids.some((value) => !!value && !!t.completedBy[value]);
}

/**
 * THE single definition of "still open for me" on a task - shared by the
 * home "Open tasks for you" summary (useMyOpenTasks) and the /tasks page's
 * My tasks view, so a count on one screen can never quietly drift from the
 * list it links to. Open means: I am assigned, and my own completedBy slot
 * is empty. A task I have snoozed still counts as open here on purpose (see
 * isTaskDoneForMe) - snoozing is "quiet me for now", not "done".
 */
export function isTaskOpenForMe(
  t: TaskDoc,
  uid: string | null | undefined | readonly string[]
): boolean {
  if (!uid) return false;
  const uids = typeof uid === 'string' ? [uid] : uid;
  if (uids.length === 0) return false;
  return taskAssignedToViewer(t, uids) && !isTaskDoneForMe(t, uids);
}

export interface UseTasksResult {
  loading: boolean;
  tasks: TaskDoc[];
  error: string | null;
  /**
   * True once at least one snapshot for THIS listener has been confirmed by
   * the server (metadata.fromCache === false) - not just served from
   * Firestore's persistentLocalCache. `loading:false` is not proof the data
   * is current: a listener can resolve instantly from a cached copy of this
   * exact query that predates writes made elsewhere in the same session (a
   * different page's listener, another device). A consumer that renders a
   * confident number off `tasks` - a home badge, a "you have N to-dos" line -
   * should treat `confirmed:false` as "still catching up" rather than trust
   * a cache-only count. Stays true once set; a listener that goes offline
   * after confirming does not un-confirm its last known state.
   */
  confirmed: boolean;
}

/** Firestore's `in` operator takes a bounded list; ten is the conservative
 *  size this codebase already uses for batched id lookups. */
export const TASK_RESPONSE_IN_BATCH = 10;

export interface UseTaskResponsesResult {
  loading: boolean;
  responsesByTaskId: Record<string, Record<string, TaskResponseDoc>>;
  error: string | null;
}

/**
 * What the To-dos page says when a read behind it failed (B46). The hooks
 * keep the SDK's own text for the console; the page never shows it, because
 * it can carry a Firebase console link and means nothing to the person
 * reading it. The list failing is said first: a missing list is the bigger
 * news than missing answers on a list that loaded.
 */
export function tasksReadFailedCopy(input: {
  listError: string | null | undefined;
  responseError: string | null | undefined;
}): string | null {
  if (input.listError) {
    return 'Your to-do list did not load just now, so some to-dos may be missing. Tap Try again.';
  }
  if (input.responseError) {
    return 'The answers on these to-dos did not load just now, so this is unknown rather than none. Tap Try again.';
  }
  return null;
}

function isTaskResponsePermissionError(err: { code?: string; message?: string }): boolean {
  return err.code === 'permission-denied'
    || /missing or insufficient permissions|permission-denied/i.test(err.message ?? '');
}

/**
 * Is this to-do one THIS viewer may see while it waits for its Show on day?
 * Only the person who made it and the Directors (packet CX-2: "the person who
 * made it and Directors see it in their own lists labeled plainly"). Everyone
 * else, staff included, never sees a waiting to-do anywhere, which is what
 * keeps a teacher on the to-do from reading it early in the studio-wide list.
 *
 * `viewerUids` is EVERY sign-in the viewer holds (jrStaffAliasUidsFor): a Jr
 * Staff is one person however she signs in (DECISIONS 26 and 27), so a to-do
 * she made on her staff account is hers on her dancer sign-in too.
 */
export function canSeeWaitingTask(
  t: Pick<TaskDoc, 'createdBy'>,
  viewerUids: string | null | undefined | readonly string[],
  viewerIsDirector: boolean
): boolean {
  if (viewerIsDirector) return true;
  const uids = typeof viewerUids === 'string' ? [viewerUids] : viewerUids ?? [];
  return uids.some((uid) => !!uid && t.createdBy === uid);
}

/**
 * A FAMILY'S OWN TO-DO IS NOT STUDIO BUSINESS (N20 refute round 1). The
 * studio-wide board, the Command Center, the front desk's lists and every
 * count built on useAllTasks (lib/tasks.ts) leave out a to-do a parent put on
 * her own family (lib/familyTodoWrite.mjs stamps familyTodo: true), unless
 * the viewer is on it or made it. This is what the screens show, not a
 * permission: the tasks read rule still lets staff read the document, as it
 * lets them read every to-do. The Assistant's studio snapshot applies the
 * same test server side (netlify/functions/lib/studioSnapshot.mjs).
 */
export function isStudioWideHiddenTask(
  t: { familyTodo?: boolean; assigneeUids: readonly string[]; createdBy: string },
  uid: string,
): boolean {
  return t.familyTodo === true && !t.assigneeUids.includes(uid) && t.createdBy !== uid;
}

/**
 * Subscribe to the tasks the current user can read. Staff read the studio
 * sees nothing for now — Phase 4 will add per-family scoping if we
 * task board. Family users use an assignee-scoped query so Firestore rules
 * can prove every returned task belongs to that signed-in family account.
 *
 * A TO-DO WAITING FOR ITS SHOW ON DAY IS LEFT OUT of every list this hook
 * returns (packet CX-2). Seven screens read the studio-wide list through it
 * (see the long note below), and every one of them would otherwise count or
 * show a to-do nobody is meant to see yet. The To-dos page is the one place
 * it shows, labeled, and only to the person who made it and the Directors:
 * it passes includeWaiting and then decides per viewer with
 * canSeeWaitingTask over every sign-in the viewer holds, which only that page
 * knows. A family never receives one at all: its query asks for "assigned to
 * me", and a waiting to-do is assigned to nobody until its morning.
 */
export function useAllTasks(includeWaiting = false): UseTasksResult {
  const { user, loading: authLoading } = useAuth();
  const { userDoc, realRole, realUserDoc, loading: meLoading } = useUserDoc();
  // A JR STAFF IS NOT ON THE STUDIO-WIDE BOARD (B2; Bobbie 2026-08-23 and
  // 2026-08-28: "only tasks assigned to them"). The rules now refuse her that
  // list, so she reads what is hers: assigned to her, made by her, and the
  // same two for her other sign-in through the jrStaffReads lane (a Jr Staff
  // is one person, DECISIONS 26 and 27). Keyed on the REAL role: a Director
  // previewing Jr Staff still reads as a Director.
  const asJrStaff = realRole === 'ensemble' && realUserDoc?.status === 'active';
  const [tasks, setTasks] = useState<TaskDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const readEpoch = useReadEpoch();

  useEffect(() => {
    if (authLoading || meLoading) return;
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      return;
    }
    if (!user || !userDoc || userDoc.status !== 'active') {
      setTasks([]);
      setError(null);
      setLoading(false);
      setConfirmed(false);
      return;
    }
    /**
     * THIS QUERY IS DELIBERATELY UNBOUNDED, and a `limit()` here is a bug.
     *
     * A newest-first cap was added here during the 2026-09-08 quota work and
     * removed in the same PR's refute round. SEVEN screens share this hook
     * and only the To-dos page could have disclosed a cap: Tasks.tsx,
     * Search.tsx, landing/AdminHome.tsx, lib/headsUpData.ts,
     * CommandCenter.tsx, Messages.tsx and FamilyDancerProfile.tsx. The other
     * six would each have shown a silently short answer.
     *
     * AdminHome's PaymentsWaitingList is the sharpest case, and the reason
     * this comment is long: it takes the top 3 OPEN payment-needed tasks
     * studio-wide sorted by dueAt ASCENDING, so the most overdue unpaid
     * family payment is exactly the document a createdAt-desc cap drops
     * first, on the front desk screen, with no line saying so.
     *
     * A cap on the right axis is not available today either. "Done" is not
     * stored: isTaskDone derives it from `completedBy` covering every uid in
     * `assigneeUids`, and Firestore cannot filter on that. Bounding on
     * createdAt therefore lets 200 newer COMPLETED to-dos crowd out an older
     * still-OPEN one, which is the To-dos page's whole job. Giving tasks a
     * stored completion field would need a rules change (hasTaskCreateShape
     * is a hasOnlyFields list), a backfill and a composite index, so it is
     * its own reviewed PR rather than a line here.
     */
    if (asJrStaff) {
      return subscribeJrStaffTasks({
        firestore: db,
        uid: user.uid,
        includeWaiting,
        withAliases: typeof realUserDoc?.studentId === 'string' && !!realUserDoc.studentId,
        setTasks,
        setLoading,
        setError,
        setConfirmed,
      });
    }
    const staff = isStaffRole(userDoc.role);
    const q = staff
      ? query(collection(db, 'tasks'), orderBy('createdAt', 'desc'))
      : query(
          collection(db, 'tasks'),
          where('assigneeUids', 'array-contains', user.uid),
          orderBy('createdAt', 'desc')
        );
    // Cache normalized docs by id across snapshots so a write to ONE task
    // doesn't hand every OTHER (unchanged) task a brand-new object identity.
    // docChanges() tells us exactly which docs actually changed; reusing the
    // same TaskDoc reference for everyone else lets TaskRow's React.memo skip
    // re-rendering rows nothing happened to, which matters once the
    // studio-wide list runs into the hundreds of tasks.
    const cache = new Map<string, TaskDoc>();
    setConfirmed(false);
    const unsub = onSnapshot(
      q,
      // includeMetadataChanges: a listener that resolves instantly off the
      // persistentLocalCache would otherwise never fire again once the
      // server confirms the SAME data, so `confirmed` could stay false
      // forever even though nothing was ever wrong. This asks for that
      // metadata-only redelivery too, purely to flip `confirmed`; the
      // per-doc cache below still only reprocesses on docChanges() (default,
      // content changes only), so an unconfirmed-to-confirmed transition
      // with no real data change costs one cheap re-render, not a rebuild.
      { includeMetadataChanges: true },
      (snap) => {
        for (const change of snap.docChanges()) {
          if (change.type === 'removed') {
            cache.delete(change.doc.id);
          } else {
            cache.set(change.doc.id, normalizeTaskDoc(change.doc.id, change.doc.data()));
          }
        }
        const liveIds = new Set(snap.docs.map((d) => d.id));
        for (const id of cache.keys()) {
          if (!liveIds.has(id)) cache.delete(id);
        }
        setTasks(
          snap.docs
            .map((d) => cache.get(d.id) ?? normalizeTaskDoc(d.id, d.data()))
            .filter((t) => includeWaiting || !isTaskWaitingToShow(t))
            .filter((t) => !isStudioWideHiddenTask(t, user.uid))
        );
        setLoading(false);
        // A resubscribe that leaves `error` set delivers live tasks
        // underneath a failed state that never clears. See lib/readEpoch.ts.
        setError(null);
        if (!snap.metadata.fromCache) setConfirmed(true);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
    // readEpoch: an onSnapshot error kills the listener for good. This is
    // the only thing that builds it again, and it only moves under a visible
    // failed state. See lib/readEpoch.ts.
  }, [authLoading, meLoading, user, userDoc, readEpoch, includeWaiting, asJrStaff, realUserDoc?.studentId]);

  return { loading, tasks, error, confirmed };
}

/**
 * useAllTasks for a Jr Staff: two rules-provable listeners (assigned to me,
 * made by me) plus one lane fetch for her other sign-in, merged by id and
 * newest first. `confirmed` waits for both listeners' server answers and for
 * the lane. A lane failure is shown as an error rather than a quietly short
 * list. Returns the cleanup.
 */
function subscribeJrStaffTasks(input: {
  firestore: NonNullable<typeof db>;
  uid: string;
  includeWaiting: boolean;
  withAliases: boolean;
  setTasks: (tasks: TaskDoc[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setConfirmed: (confirmed: boolean) => void;
}): () => void {
  const { firestore, uid, includeWaiting } = input;
  const assigned = new Map<string, TaskDoc>();
  const made = new Map<string, TaskDoc>();
  const alias = new Map<string, TaskDoc>();
  const seen = { assigned: false, made: false, alias: !input.withAliases };
  const serverConfirmed = { assigned: false, made: false };
  let cancelled = false;
  const errors: Record<string, string | null> = { assigned: null, made: null, alias: null };
  const publish = () => {
    if (cancelled) return;
    const merged = new Map<string, TaskDoc>([...alias, ...made, ...assigned]);
    input.setTasks(
      Array.from(merged.values())
        .filter((t) => includeWaiting || !isTaskWaitingToShow(t))
        .sort((a, b) => tsToMillisOrZero(b.createdAt) - tsToMillisOrZero(a.createdAt))
    );
    const firstError = errors.assigned ?? errors.made ?? errors.alias;
    input.setError(firstError);
    if (seen.assigned && seen.made && seen.alias) input.setLoading(false);
    input.setConfirmed(serverConfirmed.assigned && serverConfirmed.made && seen.alias && !errors.alias);
  };
  const listen = (key: 'assigned' | 'made', target: Map<string, TaskDoc>, q: ReturnType<typeof query>) =>
    onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        target.clear();
        for (const d of snap.docs) target.set(d.id, normalizeTaskDoc(d.id, d.data()));
        seen[key] = true;
        errors[key] = null;
        if (!snap.metadata.fromCache) serverConfirmed[key] = true;
        publish();
      },
      (err) => {
        seen[key] = true;
        errors[key] = err.message;
        publish();
      }
    );
  const unsubs = [
    listen('assigned', assigned, query(
      collection(firestore, 'tasks'),
      where('assigneeUids', 'array-contains', uid),
      orderBy('createdAt', 'desc')
    )),
    // One equality filter and no orderBy: the automatic single-field index
    // serves it, the same shape the room search's twin-request check uses.
    listen('made', made, query(collection(firestore, 'tasks'), where('createdBy', '==', uid))),
  ];
  const loadAlias = () => {
    void loadJrStaffAliasTasks()
      .then((rows) => {
        alias.clear();
        for (const row of rows) alias.set(row.id, normalizeTaskDoc(row.id, row.data));
        errors.alias = null;
      })
      .catch((err: unknown) => {
        errors.alias = err instanceof Error ? err.message : 'Could not load your other sign-in\'s to-dos.';
      })
      .finally(() => {
        seen.alias = true;
        publish();
      });
  };
  if (input.withAliases) {
    loadAlias();
    // Her other sign-in's to-dos are a one-time copy, so a tick made through
    // the lane (useCompleteTask) asks for a fresh copy rather than leaving
    // the to-do open on screen.
    jrStaffAliasTaskRefreshers.add(loadAlias);
  }
  return () => {
    cancelled = true;
    jrStaffAliasTaskRefreshers.delete(loadAlias);
    unsubs.forEach((unsub) => unsub());
  };
}

const jrStaffAliasTaskRefreshers = new Set<() => void>();
const jrStaffAliasAnswerRefreshers = new Set<() => void>();

/**
 * Fetch her other sign-in's to-dos, and their answers, again on every open
 * Jr Staff list. Both are one-time lane copies, so a tick, a snooze or an
 * answer written through the lane calls this rather than leaving the old copy
 * on screen.
 */
export function refreshJrStaffOtherSignIn(): void {
  for (const refresh of Array.from(jrStaffAliasTaskRefreshers)) refresh();
  for (const refresh of Array.from(jrStaffAliasAnswerRefreshers)) refresh();
}

function tsToMillisOrZero(value: unknown): number {
  return value && typeof (value as { toMillis?: unknown }).toMillis === 'function'
    ? (value as { toMillis: () => number }).toMillis()
    : 0;
}

/**
 * The bounded home-screen task read (perf round, audit F3/#2).
 *
 * useAllTasks' staff branch syncs the ENTIRE tasks collection, all-time,
 * which the Director home was paying for just to show a top-3 summary and
 * a couple of tile badges. This hook asks only for tasks assigned to ME,
 * newest first, capped at `max` (default 150):
 *
 *   where('assigneeUids','array-contains', uid)
 *     + orderBy('createdAt','desc') + limit(max)
 *
 * That exact query shape is already live in production for family
 * accounts (useAllTasks' non-staff branch), so the composite index
 * (assigneeUids CONTAINS + createdAt DESC in shared/firestore.indexes.json)
 * and the assignee read branch in the deployed rules both cover it -
 * no new index, no rules change. Staff reads pass the resource-independent
 * staff branch as well.
 *
 * KNOWN CAP SEMANTICS: an OPEN task older than my newest 150 assigned
 * tasks falls off the home summary and tile badges. It still shows on
 * /tasks, which keeps the unbounded useAllTasks read on purpose.
 *
 * `enabled` (perf round, home-listener-dedupe): mirrors useUpcomingSubRequests'
 * param of the same name. Defaults true, so every existing caller keeps its
 * exact subscription. Pass false when a parent already mounted this same
 * query and handed the result down as props instead - the hook call itself
 * still happens every render (Rules of Hooks), it just skips the onSnapshot
 * subscribe and settles to loading:false/tasks:[]/confirmed:false, same as
 * the disabled shape useUpcomingSubRequests returns.
 */
export function useMyAssignedTasks(max = 150, enabled = true): UseTasksResult {
  const { user, loading: authLoading } = useAuth();
  const { userDoc, loading: meLoading } = useUserDoc();
  const [tasks, setTasks] = useState<TaskDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const readEpoch = useReadEpoch();

  useEffect(() => {
    if (!enabled) {
      setTasks([]);
      setLoading(false);
      setError(null);
      setConfirmed(false);
      return;
    }
    // Back to true on every enabled-flip, mirroring useUpcomingSubRequests'
    // identical guard: without it a re-enabled hook sits at loading false
    // with an empty list until the first snapshot - an honest-looking
    // settled empty. No current caller flips enabled at runtime, but the
    // sibling hook's dynamic-enabled pattern is one copy-paste away
    // (review finding, 2026-08-19).
    setLoading(true);
    if (authLoading || meLoading) return;
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      return;
    }
    if (!user || !userDoc || userDoc.status !== 'active') {
      setTasks([]);
      setError(null);
      setLoading(false);
      setConfirmed(false);
      return;
    }
    const q = query(
      collection(db, 'tasks'),
      where('assigneeUids', 'array-contains', user.uid),
      orderBy('createdAt', 'desc'),
      limit(max)
    );
    // Same docChanges()-keyed identity cache as useAllTasks: a write to ONE
    // task must not hand every other unchanged task a new object identity.
    const cache = new Map<string, TaskDoc>();
    setConfirmed(false);
    const unsub = onSnapshot(
      q,
      // See useAllTasks - this is a SEPARATE listener target from that hook's
      // (this one is scoped + capped, that one is the studio-wide board), so
      // it needs its own confirmed tracking rather than inheriting one. A
      // home widget built on THIS hook must not read a cache-only snapshot
      // as proof its "N open to-dos" count is current.
      { includeMetadataChanges: true },
      (snap) => {
        for (const change of snap.docChanges()) {
          if (change.type === 'removed') {
            cache.delete(change.doc.id);
          } else {
            cache.set(change.doc.id, normalizeTaskDoc(change.doc.id, change.doc.data()));
          }
        }
        const liveIds = new Set(snap.docs.map((d) => d.id));
        for (const id of cache.keys()) {
          if (!liveIds.has(id)) cache.delete(id);
        }
        setTasks(
          snap.docs
            .map((d) => cache.get(d.id) ?? normalizeTaskDoc(d.id, d.data()))
            // Belt and braces: a waiting to-do is assigned to nobody, so this
            // assignee query cannot return one (packet CX-2). Should one ever
            // carry a name early, it still stays off every home count.
            .filter((t) => !isTaskWaitingToShow(t))
        );
        setLoading(false);
        // A resubscribe that leaves `error` set delivers live tasks
        // underneath a failed state that never clears. See lib/readEpoch.ts.
        setError(null);
        if (!snap.metadata.fromCache) setConfirmed(true);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
    // readEpoch: an onSnapshot error kills the listener for good. This is
    // the only thing that builds it again, and it only moves under a visible
    // failed state. See lib/readEpoch.ts.
  }, [authLoading, meLoading, user, userDoc, max, readEpoch, enabled]);

  return { loading, tasks, error, confirmed };
}

/**
 * Tasks assigned to an arbitrary OTHER uid, bounded the same way
 * useMyAssignedTasks bounds the signed-in caller's own tasks.
 *
 * Only meaningful for a Director previewing a real family's read-only home
 * screen (see FamilyCommandCenter.tsx): useMyAssignedTasks always reads the
 * SIGNED-IN caller's own uid, which during a preview is the Director, not
 * the family being looked at. This hook takes the target uid explicitly.
 *
 * Provable under the deployed rules without any change: the tasks read rule
 * grants active staff a RESOURCE-INDEPENDENT branch (isActiveStaffByDoc()),
 * so a staff caller's list query can be filtered by any assigneeUids value
 * and still be proven safe - narrowing an already-allowed bare list-all can
 * only ever remove documents, never grant a new one. A non-staff caller
 * passing a uid here would simply be denied by the same rule, exactly as
 * useAllTasks' non-staff branch already relies on for the caller's own uid.
 */
export function usePreviewAssignedTasks(uid: string | null, max = 150): UseTasksResult {
  const { user, loading: authLoading } = useAuth();
  const { userDoc, loading: meLoading } = useUserDoc();
  const [tasks, setTasks] = useState<TaskDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const readEpoch = useReadEpoch();

  useEffect(() => {
    if (authLoading || meLoading) return;
    if (!db || !user || !userDoc || userDoc.status !== 'active' || !uid) {
      setTasks([]);
      setError(null);
      setLoading(false);
      setConfirmed(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, 'tasks'),
      where('assigneeUids', 'array-contains', uid),
      orderBy('createdAt', 'desc'),
      limit(max)
    );
    const cache = new Map<string, TaskDoc>();
    setConfirmed(false);
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        for (const change of snap.docChanges()) {
          if (change.type === 'removed') {
            cache.delete(change.doc.id);
          } else {
            cache.set(change.doc.id, normalizeTaskDoc(change.doc.id, change.doc.data()));
          }
        }
        const liveIds = new Set(snap.docs.map((d) => d.id));
        for (const id of cache.keys()) {
          if (!liveIds.has(id)) cache.delete(id);
        }
        setTasks(
          snap.docs
            .map((d) => cache.get(d.id) ?? normalizeTaskDoc(d.id, d.data()))
            // Belt and braces: a waiting to-do is assigned to nobody, so this
            // assignee query cannot return one (packet CX-2). Should one ever
            // carry a name early, it still stays off every home count.
            .filter((t) => !isTaskWaitingToShow(t))
        );
        setLoading(false);
        // A resubscribe that leaves `error` set delivers live tasks
        // underneath a failed state that never clears. See lib/readEpoch.ts.
        setError(null);
        if (!snap.metadata.fromCache) setConfirmed(true);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
    // readEpoch: an onSnapshot error kills the listener for good. This is
    // the only thing that builds it again, and it only moves under a visible
    // failed state. See lib/readEpoch.ts.
  }, [authLoading, meLoading, user, userDoc, uid, max, readEpoch]);

  return { loading, tasks, error, confirmed };
}

/** One response document as every screen takes it. */
function taskResponseFromRaw(
  id: string,
  taskId: string,
  raw: Partial<Omit<TaskResponseDoc, 'id'>>,
  fallbackUid: string
): TaskResponseDoc | null {
  const uid = typeof raw.uid === 'string' && raw.uid ? raw.uid : fallbackUid;
  if (!uid) return null;
  const rawKind = raw.kind;
  const kind: TaskResponseKind = rawKind === 'general'
    || rawKind === 'upload-music'
    || rawKind === 'availability-poll'
    || rawKind === 'choice-poll'
    ? rawKind
    : 'upload-file';
  return {
    id,
    taskId,
    uid,
    kind,
    url: typeof raw.url === 'string' ? raw.url : '',
    label: typeof raw.label === 'string' && raw.label.trim() ? raw.label : 'Response',
    note: typeof raw.note === 'string' ? raw.note : undefined,
    selectedOptionIds: normalizeSelectedOptionIds(raw.selectedOptionIds),
    attachments: normalizeTaskResponseAttachments(raw.attachments),
    savedAt: raw.savedAt as Timestamp | FieldValue,
    updatedAt: raw.updatedAt,
  };
}

/**
 * What a Jr Staff's To-dos page knows about the to-dos in hand, so her answers
 * read matches what the rules give her (refute round on B2). Ignored for every
 * other role.
 */
export interface TaskResponsesScope {
  /** To-dos she made on THIS sign-in: she reads every answer, live. */
  madeByMeTaskIds?: readonly string[];
  /**
   * To-dos made by, or given to, her OTHER sign-in. The rules cannot see
   * that sign-in, so their answers come from the jrStaffReads lane, once per
   * page load (a Jr Staff is one person, DECISIONS 26 and 27).
   */
  otherSignInTaskIds?: readonly string[];
}

/**
 * Per-assignee task responses live under tasks/{taskId}/responses/{uid}.
 * This keeps family uploads and availability notes private to that family,
 * while staff can still review every response from the studio task board.
 */
export function useTaskResponses(taskIds: string[], scope: TaskResponsesScope = {}): UseTaskResponsesResult {
  const { user, loading: authLoading } = useAuth();
  const { userDoc, realRole, loading: meLoading } = useUserDoc();
  const [loading, setLoading] = useState(true);
  const [responsesByTaskId, setResponsesByTaskId] = useState<Record<string, Record<string, TaskResponseDoc>>>({});
  const [error, setError] = useState<string | null>(null);
  const taskIdsKey = taskIds.slice().sort().join('|');
  const madeKey = (scope.madeByMeTaskIds ?? []).slice().sort().join('|');
  const otherKey = (scope.otherSignInTaskIds ?? []).slice().sort().join('|');
  // A failed onSnapshot is dead for good. Try again on the To-dos page bumps
  // this and is the only thing that builds the answers listeners again (B46).
  const readEpoch = useReadEpoch();

  useEffect(() => {
    if (authLoading || meLoading) return;
    if (!db) {
      setResponsesByTaskId({});
      setLoading(false);
      setError('Firestore not initialized.');
      return;
    }
    const firestore = db;
    if (!user || !userDoc || userDoc.status !== 'active') {
      setResponsesByTaskId({});
      setLoading(false);
      return;
    }
    const allowedTaskIds = new Set(taskIdsKey.split('|').filter(Boolean));
    if (allowedTaskIds.size === 0) {
      setResponsesByTaskId({});
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // A FAMILY reads only her own answer. A JR STAFF (B2) reads her own
    // answer too, plus EVERY answer on a to-do she made (the rules' maker
    // branch, isTaskResponseParentMaker), plus, through the lane, the answers
    // on her other sign-in's to-dos. She never gets the studio-wide group
    // read: the rules refuse 'ensemble' that since B2.
    if (!isStaffRole(userDoc.role) || realRole === 'ensemble') {
      const asJrStaff = realRole === 'ensemble';
      const madeByMe = new Set(
        asJrStaff ? madeKey.split('|').filter((id) => allowedTaskIds.has(id)) : []
      );
      const viaLane = asJrStaff
        ? otherKey.split('|').filter((id) => allowedTaskIds.has(id) && !madeByMe.has(id))
        : [];
      const own = new Map<string, TaskResponseDoc>();
      const all = new Map<string, Record<string, TaskResponseDoc>>();
      const lane = new Map<string, Record<string, TaskResponseDoc>>();
      const errors = new Map<string, string>();
      const settled = new Set<string>();
      const expected = allowedTaskIds.size + (viaLane.length > 0 ? 1 : 0);
      let cancelled = false;
      const publish = () => {
        if (cancelled) return;
        const next: Record<string, Record<string, TaskResponseDoc>> = {};
        for (const taskId of allowedTaskIds) {
          const mine = own.get(taskId);
          const merged: Record<string, TaskResponseDoc> = {
            ...(lane.get(taskId) ?? {}),
            ...(mine ? { [mine.uid]: mine } : {}),
            ...(all.get(taskId) ?? {}),
          };
          if (Object.keys(merged).length > 0) next[taskId] = merged;
        }
        setResponsesByTaskId(next);
        setError(errors.size > 0 ? Array.from(errors.values())[0] : null);
        if (settled.size >= expected) setLoading(false);
      };
      const unsubs = Array.from(allowedTaskIds).map((taskId) => {
        if (madeByMe.has(taskId)) {
          return onSnapshot(
            collection(firestore, 'tasks', taskId, 'responses'),
            (snap) => {
              const record: Record<string, TaskResponseDoc> = {};
              for (const d of snap.docs) {
                const built = taskResponseFromRaw(d.id, taskId, d.data() as Partial<Omit<TaskResponseDoc, 'id'>>, d.id);
                if (built) record[built.uid] = built;
              }
              all.set(taskId, record);
              errors.delete(taskId);
              settled.add(taskId);
              publish();
            },
            (err) => {
              // Not swallowed: the rules give the maker this list, so a
              // refusal here is a real fault she should see, not "no answers".
              errors.set(taskId, err.message);
              settled.add(taskId);
              publish();
            }
          );
        }
        return onSnapshot(
          doc(firestore, 'tasks', taskId, 'responses', user.uid),
          (responseSnap) => {
            const built = responseSnap.exists()
              ? taskResponseFromRaw(
                responseSnap.id,
                taskId,
                responseSnap.data() as Partial<Omit<TaskResponseDoc, 'id'>>,
                user.uid
              )
              : null;
            if (built) own.set(taskId, { ...built, uid: user.uid });
            else own.delete(taskId);
            settled.add(taskId);
            publish();
          },
          (err) => {
            if (!isTaskResponsePermissionError(err)) errors.set(taskId, err.message);
            settled.add(taskId);
            publish();
          }
        );
      });
      const loadLane = () => {
        void loadJrStaffAliasTaskResponses(viaLane)
          .then((rows) => {
            lane.clear();
            errors.delete('lane');
            for (const row of rows) {
              const built = taskResponseFromRaw(
                row.uid,
                row.taskId,
                row.data as Partial<Omit<TaskResponseDoc, 'id'>>,
                row.uid
              );
              if (!built) continue;
              lane.set(row.taskId, { ...(lane.get(row.taskId) ?? {}), [built.uid]: built });
            }
          })
          .catch((err: unknown) => {
            errors.set('lane', err instanceof Error ? err.message : 'Could not load the answers right now.');
          })
          .finally(() => {
            settled.add('lane');
            publish();
          });
      };
      if (viaLane.length > 0) {
        loadLane();
        // A lane copy, so an answer saved through the lane asks for a fresh
        // one (refreshJrStaffOtherSignIn).
        jrStaffAliasAnswerRefreshers.add(loadLane);
      }
      return () => {
        cancelled = true;
        jrStaffAliasAnswerRefreshers.delete(loadLane);
        unsubs.forEach((unsub) => unsub());
      };
    }

    // THE STUDIO-WIDE RESPONSES READ, now scoped to the tasks in hand.
    //
    // This was `query(collectionGroup(firestore, 'responses'))`: no where, no
    // limit, every response document every family has ever filed against
    // every task, read and held open on the To-dos page and on the dancer
    // profile task cards. The result was then thrown away for any taskId not
    // in `allowedTaskIds` about twenty lines below, so the extra documents
    // never reached a screen at all. One of the reads behind the daily quota
    // the Hub hit on the class night of 2026-09-08, spent on nothing.
    //
    // Every response document carries its own `taskId`, enforced by the
    // rules on create AND update (shared/firestore.rules,
    // hasTaskResponseBaseShape: `data.taskId == taskId`), so an `in` filter
    // on it is exact rather than best-effort. Firestore caps an `in` list, so
    // the ids go out in batches; each batch is its own listener and they
    // share one cache and one published record, exactly as the single
    // listener did.
    const responseBatches: string[][] = [];
    for (const taskId of allowedTaskIds) {
      const last = responseBatches[responseBatches.length - 1];
      if (last && last.length < TASK_RESPONSE_IN_BATCH) last.push(taskId);
      else responseBatches.push([taskId]);
    }

    // Cache the per-task response record across snapshots (same idea as the
    // useAllTasks doc cache above): a response write for ONE task should not
    // hand every OTHER task's record a new object identity, or every
    // studio-wide TaskRow re-renders on every single family upload/response.
    // docChanges() tells us which task(s) actually changed this snapshot; we
    // rebuild only those tasks' records and keep the rest untouched.
    const responseCache = new Map<string, Record<string, TaskResponseDoc>>();
    const resolveTaskId = (
      raw: Partial<Omit<TaskResponseDoc, 'id'>>,
      ref: { parent: { parent: { id: string } | null } }
    ): string =>
      typeof raw.taskId === 'string' ? raw.taskId : ref.parent.parent?.id ?? '';
    const buildResponse = (
      responseSnap: QueryDocumentSnapshot<DocumentData>,
      taskId: string
    ): TaskResponseDoc | null => {
      const raw = responseSnap.data() as Partial<Omit<TaskResponseDoc, 'id'>>;
      const uid = typeof raw.uid === 'string' && raw.uid ? raw.uid : responseSnap.id;
      if (!uid) return null;
      const rawKind = raw.kind;
      const kind: TaskResponseKind = rawKind === 'general'
        || rawKind === 'upload-music'
        || rawKind === 'availability-poll'
        || rawKind === 'choice-poll'
        ? rawKind
        : 'upload-file';
      return {
        id: responseSnap.id,
        taskId,
        uid,
        kind,
        url: typeof raw.url === 'string' ? raw.url : '',
        label: typeof raw.label === 'string' && raw.label.trim() ? raw.label : 'Response',
        note: typeof raw.note === 'string' ? raw.note : undefined,
        selectedOptionIds: normalizeSelectedOptionIds(raw.selectedOptionIds),
        attachments: normalizeTaskResponseAttachments(raw.attachments),
        savedAt: raw.savedAt as Timestamp | FieldValue,
        updatedAt: raw.updatedAt,
      };
    };

    const settledBatches = new Set<number>();
    const unsubs = responseBatches.map((batch, batchIndex) => onSnapshot(
      query(collectionGroup(firestore, 'responses'), where('taskId', 'in', batch)),
      (snap) => {
        // One pass to group every current doc by taskId (cheap, O(n)); then
        // only the taskIds docChanges() flags as touched get their record
        // rebuilt, so a single check-off doesn't redo work for the whole list.
        const docsByTaskId = new Map<string, QueryDocumentSnapshot<DocumentData>[]>();
        for (const d of snap.docs) {
          const raw = d.data() as Partial<Omit<TaskResponseDoc, 'id'>>;
          const taskId = resolveTaskId(raw, d.ref);
          if (!taskId) continue;
          const arr = docsByTaskId.get(taskId);
          if (arr) arr.push(d);
          else docsByTaskId.set(taskId, [d]);
        }
        const touchedTaskIds = new Set<string>();
        for (const change of snap.docChanges()) {
          const raw = change.doc.data() as Partial<Omit<TaskResponseDoc, 'id'>>;
          const taskId = resolveTaskId(raw, change.doc.ref);
          if (taskId) touchedTaskIds.add(taskId);
        }
        for (const taskId of touchedTaskIds) {
          const docsForTask = docsByTaskId.get(taskId) ?? [];
          if (docsForTask.length === 0) {
            responseCache.delete(taskId);
            continue;
          }
          const record: Record<string, TaskResponseDoc> = {};
          for (const responseSnap of docsForTask) {
            const built = buildResponse(responseSnap, taskId);
            if (built) record[built.uid] = built;
          }
          responseCache.set(taskId, record);
        }
        const next: Record<string, Record<string, TaskResponseDoc>> = {};
        for (const taskId of allowedTaskIds) {
          const rec = responseCache.get(taskId);
          if (rec) next[taskId] = rec;
        }
        setResponsesByTaskId(next);
        // Every batch has to answer before this list is complete; saying
        // "loaded" after the first one would draw a page missing the
        // responses the other batches are still carrying.
        settledBatches.add(batchIndex);
        if (settledBatches.size >= responseBatches.length) setLoading(false);
      },
      (err) => {
        if (isTaskResponsePermissionError(err)) {
          setError(null);
          setResponsesByTaskId({});
          setLoading(false);
          return;
        }
        setError(err.message);
        setResponsesByTaskId({});
        setLoading(false);
      }
    ));
    return () => unsubs.forEach((unsub) => unsub());
  }, [authLoading, meLoading, taskIdsKey, madeKey, otherKey, user, userDoc, realRole, readEpoch]);

  return { loading, responsesByTaskId, error };
}

export interface NewTaskInput {
  title: string;
  description: string;
  assigneeUids: string[];
  studentIds?: string[];
  dueAt: Date | null;
  recurrence: Recurrence;
  kind?: TaskKind;
  /**
   * Which preference category controls the task alert. The task's own `kind`
   * still controls its UI; this only lets purpose-built flows such as the
   * Recital/Comp Tracker honor the recipient's Competition & recital switch.
   */
  notificationKind?: Extract<NotifKind, 'task' | 'comp'>;
  /** False when the parent studio action already sent its one canonical alert. */
  notifyAssignees?: boolean;
  timeOptions?: TaskTimeOption[];
  choiceOptions?: TaskChoiceOption[];
  source?: TaskDoc['source'];
  attachments?: TaskAttachment[];
  /**
   * PACKET CX-2. A studio day ('YYYY-MM-DD') after today: the to-do waits,
   * assigned to nobody, and sends no alert until that morning. Null or absent
   * shows it now, as before.
   */
  showOn?: string | null;
  /** PACKET CX-2. Reminders N days before the due date (90/60/30/15/7). */
  reminderDaysBefore?: number[];
  /**
   * A reminder made from a class post buzzes 7pm the night before, 9am that
   * day and an hour before (lib/taskAlerts.ts). Server only, so it routes
   * through the taskWrite door like a schedule does.
   */
  timedAlerts?: boolean;
  /**
   * Reuse an id already minted by newTaskId(). The composer needs one before
   * the doc exists so photos can be uploaded into this task's own Storage
   * folder; without it the upload would have nowhere scoped to go.
   */
  id?: string;
}

/**
 * Bobbie REVERSED the first version of this rule the same day it was built.
 * First: "Only directors and managers can assign to someone else." Then, on
 * 2026-08-13: "Parents can't create tasks for teachers. But staff can make
 * tasks for other staff", "And staff can assign to dancers and parents", and
 * "Yes jr staff count as staff in this case".
 *
 * So the boundary is PARENT versus STAFF, one direction: staff assign,
 * families receive. Every staff role assigns; a parent or dancer makes tasks
 * for themselves and nobody else. The reasoning lives in DECISIONS.md under
 * WHO CAN PUT A TASK ON SOMEBODY ELSE.
 *
 * Naming the roles once here keeps the screen honest about a boundary it
 * does not own. The decision is enforced twice on the way in - in
 * `shared/firestore.rules` for a plain write and in
 * `netlify/functions/lib/taskWrite.mjs` for a write carrying attachments - so
 * this constant only decides what to OFFER, never what to allow.
 */
export const MESSAGE_TASK_ASSIGNING_ROLES: readonly UserRole[] =
  ['director', 'manager', 'company', 'ensemble', 'foh'];

export function canAssignMessageTaskToOthers(
  userDoc: UserDoc | null | undefined
): boolean {
  return userDoc?.status === 'active'
    && MESSAGE_TASK_ASSIGNING_ROLES.includes(userDoc.role);
}

/**
 * Mint a task id up front. Only needed by the composer, which uploads
 * attachments before it saves. Everything else can let useCreateTask pick one.
 */
export function newTaskId(): string {
  if (!db) throw new Error('Firestore not initialized.');
  return doc(collection(db, 'tasks')).id;
}

export function useCreateTask() {
  const { user } = useAuth();
  const { realUserDoc } = useUserDoc();
  return async (input: NewTaskInput): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    if (!input.title.trim()) throw new Error('Title is required.');
    if (input.assigneeUids.length === 0) {
      throw new Error('Pick at least one assignee.');
    }
    if (input.recurrence !== 'none' && !input.dueAt) {
      throw new Error('Recurring to-dos need a first due date.');
    }
    const timeOptions = (input.timeOptions ?? []).slice(0, MAX_TASK_TIME_OPTIONS);
    if ((input.attachments?.length ?? 0) > MAX_TASK_ATTACHMENTS) {
      throw new Error(`You can attach up to ${MAX_TASK_ATTACHMENTS} photos, videos, files, or links.`);
    }
    const choiceOptions = (input.choiceOptions ?? [])
      .map((option) => ({ id: option.id.slice(0, 40), label: option.label.trim().slice(0, 120) }))
      .filter((option) => option.id && option.label)
      .slice(0, MAX_TASK_CHOICE_OPTIONS);
    const attachments = input.attachments ?? [];
    const showOn = input.showOn?.trim() || null;
    const reminderDaysBefore = normalizeReminderDays(input.reminderDaysBefore ?? []);
    // A Show on day or reminders are server-only fields (packet CX-2), so a
    // to-do carrying either goes through the same server door attachments do.
    const hasSchedule = Boolean(showOn) || reminderDaysBefore.length > 0;
    if (hasSchedule && input.recurrence !== 'none') {
      throw new Error('A Show on date and reminders work on one-time to-dos. Set Repeat to One-time to use them.');
    }
    // setDoc, not addDoc: the composer already minted this id and uploaded
    // photos underneath it, so the doc has to land on that exact id.
    const ref = doc(db, 'tasks', input.id ?? newTaskId());
    const taskWrite = {
      title: input.title.trim(),
      description: input.description.trim(),
      assigneeUids: Array.from(new Set(input.assigneeUids)),
      studentIds: Array.from(new Set(input.studentIds ?? [])),
      dueAt: input.dueAt ? input.dueAt : null,
      completedBy: {},
      recurrence: input.recurrence,
      nextInstanceId: null,
      kind: input.kind ?? 'general',
      uploadLinks: {},
      ...(timeOptions.length > 0 ? { timeOptions } : {}),
      ...(choiceOptions.length > 0 ? { choiceOptions } : {}),
      source: input.source ?? null,
      createdAt: serverTimestamp(),
      createdBy: user.uid,
      createdByName: realUserDoc
        ? teacherDisplayName(realUserDoc)
        : user.displayName || null,
      updatedAt: serverTimestamp(),
    };
    // True when the server held the to-do back for its Show on day; the
    // morning sweep sends its new to-do alert then, not now.
    let scheduled = false;
    const timedAlerts = input.timedAlerts === true;
    if (attachments.length > 0 || hasSchedule || timedAlerts) {
      const attachmentItems = attachments.map((attachment) => {
        const label = attachment.label.trim().slice(0, 120)
          || (attachment.kind === 'image' ? 'Photo' : 'Attachment');
        if (attachment.kind === 'link') {
          if (!isSafeAttachmentUrl(attachment.url)) {
            throw new Error('Attachment links must start with https://');
          }
          return { type: 'link' as const, url: attachment.url.trim(), label };
        }
        if (!attachment.reservationId?.trim()) {
          throw new Error('One uploaded to-do attachment is no longer ready. Choose it again.');
        }
        return {
          type: 'reservation' as const,
          reservationId: attachment.reservationId.trim(),
          label,
        };
      });
      const result = await createTaskWithAttachmentsViaServer(user, ref.id, {
        title: input.title.trim(),
        description: input.description.trim(),
        assigneeUids: Array.from(new Set(input.assigneeUids)),
        studentIds: Array.from(new Set(input.studentIds ?? [])),
        dueAt: input.dueAt?.toISOString() ?? null,
        recurrence: input.recurrence,
        kind: input.kind ?? 'general',
        timeOptions,
        source: input.source ?? null,
        attachmentItems,
        ...(choiceOptions.length > 0 ? { choiceOptions } : {}),
        ...(hasSchedule ? { showOn, reminderDaysBefore } : {}),
        ...(timedAlerts ? { timedAlerts: true as const } : {}),
      });
      scheduled = result.scheduled;
    } else {
      await setDoc(ref, taskWrite);
    }

    // Notify the assignees (in-app now; email/SMS once keys are set).
    // Best-effort: never fail task creation if delivery hiccups. A to-do
    // waiting for its Show on day tells nobody now: nobody can see it yet.
    if (input.notifyAssignees !== false && !scheduled) {
      try {
        await notifyUsers(input.assigneeUids, {
          kind: input.notificationKind ?? 'task',
          title: `New task: ${input.title.trim()}`,
          body: input.description.trim().slice(0, 140),
          link: `/tasks#task-${ref.id}`,
          taskId: ref.id,
        }, { staffScopedSource: { kind: 'task', id: ref.id } });
      } catch (e) {
        // Task is saved; delivery is best-effort - but never silent (a
        // swallowed throw here was one arm of the studio-wide "no
        // notifications for anything" silence, 2026-08-07 diagnosis).
        console.error('createTask: notify failed', e);
      }
    }
    return ref.id;
  };
}

async function saveTaskResponseAndComplete(
  taskId: string,
  user: User,
  kind: TaskResponseKind,
  response: {
    url: string;
    label: string;
    note?: string;
    selectedOptionIds?: string[];
    attachments?: TaskResponseAttachment[];
  }
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const uid = user.uid;
  const trimmedNote = response.note?.trim();
  const selections = Array.from(new Set(response.selectedOptionIds ?? [])).slice(0, 12);
  const attachments = normalizeTaskResponseAttachments(response.attachments);
  if ((response.attachments?.length ?? 0) !== (attachments?.length ?? 0)) {
    throw new Error('One response attachment was not finalized for this to-do. Remove it and try again.');
  }
  if (attachments?.length) {
    await saveAttachedTaskResponseViaServer(user, taskId, kind, {
      url: response.url,
      label: response.label,
      note: trimmedNote,
      selectedOptionIds: selections,
      attachments,
      complete: true,
    });
    await finishTaskNotificationCleanup(taskId);
    return;
  }
  const batch = writeBatch(db);
  batch.set(doc(db, 'tasks', taskId, 'responses', uid), {
    taskId,
    uid,
    kind,
    url: response.url,
    label: response.label,
    ...(trimmedNote ? { note: trimmedNote.slice(0, 1000) } : {}),
    ...(selections.length > 0 ? { selectedOptionIds: selections } : {}),
    ...(attachments?.length ? { attachments } : {}),
    savedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  batch.update(doc(db, 'tasks', taskId), {
    [`completedBy.${uid}`]: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
  await finishTaskNotificationCleanup(taskId);
}

/**
 * Post a Drive (or any URL) link as the deliverable for an upload task.
 * Saving the response also stamps completedBy.{myUid}, so the link IS the
 * completion.
 *
 * Idempotent: posting again replaces the prior response.
 */
export function useAddTaskUpload() {
  const { user } = useAuth();
  return async (
    taskId: string,
    url: string,
    label: string,
    kind: TaskResponseKind = 'upload-file'
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const trimmedUrl = url.trim();
    if (!trimmedUrl) throw new Error('Paste a link first.');
    if (!/^https?:\/\//i.test(trimmedUrl)) {
      throw new Error('Link must start with http:// or https://');
    }
    await saveTaskResponseAndComplete(taskId, user, kind, {
      url: trimmedUrl,
      label: label.trim() || 'Uploaded link',
    });
  };
}

/**
 * Save an availability response for this assignee. The response is stored in
 * that assignee's private task response doc, then the task is marked done for
 * them in the same batch.
 *
 * When the task has concrete timeOptions, selectedOptionIds carries which
 * ones the assignee tapped and note becomes an optional "anything else?"
 * field. When the task has no timeOptions (legacy free-text-only polls),
 * note is required exactly like before.
 */
export function useAddTaskAvailabilityResponse() {
  const { user } = useAuth();
  return async (
    taskId: string,
    note: string,
    selectedOptionIds: string[] = [],
    attachments: TaskResponseAttachment[] = []
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const trimmed = note.trim();
    const selections = Array.from(new Set(selectedOptionIds)).slice(0, 12);
    if (!trimmed && selections.length === 0 && attachments.length === 0) {
      throw new Error('Pick a time that works, add a note, or attach a file first.');
    }
    await saveTaskResponseAndComplete(taskId, user, 'availability-poll', {
      url: '',
      label: 'Availability',
      note: trimmed || undefined,
      selectedOptionIds: selections,
      attachments,
    });
  };
}

/** Save exactly one answer for a choice poll. Posting again replaces it. */
export function useAddTaskChoiceResponse() {
  const { user } = useAuth();
  return async (taskId: string, selectedOptionId: string, note = ''): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const optionId = selectedOptionId.trim().slice(0, 40);
    if (!optionId) throw new Error('Pick one answer first.');
    await saveTaskResponseAndComplete(taskId, user, 'choice-poll', {
      url: '',
      label: 'Poll response',
      note: note.trim() || undefined,
      selectedOptionIds: [optionId],
    });
  };
}

/**
 * Windows and some browsers report no MIME type for common audio files
 * (.m4a especially), which made Storage's audio-only rule reject music
 * uploads as application/octet-stream. Infer from the extension when the
 * browser leaves the type blank.
 */
const EXTENSION_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  m4v: 'video/mp4',
  webm: 'video/webm',
  avi: 'video/x-msvideo',
  '3gp': 'video/3gpp',
  pdf: 'application/pdf',
  m4a: 'audio/mp4',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  aac: 'audio/aac',
  ogg: 'audio/ogg',
  flac: 'audio/flac',
  aif: 'audio/aiff',
  aiff: 'audio/aiff',
  txt: 'text/plain',
  csv: 'text/csv',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

const TASK_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
// 6 GB, RESIZED 2026-08-18 alongside the gateway's own ATTACHMENT_VIDEO_MAX_BYTES
// (see attachmentUpload.mjs for the sizing math). Both task attachment hooks
// below go through uploadAttachmentViaGateway only, never a direct Storage
// SDK write, so there is no shared/storage.rules ceiling to stay under here.
const TASK_VIDEO_MAX_BYTES = 6 * 1024 * 1024 * 1024;
const TASK_FILE_MAX_BYTES = 25 * 1024 * 1024;

/** Mirrors the allowlists in Storage and Firestore rules. */
export function isSafeTaskAttachmentContentType(contentType: string): boolean {
  return [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/heic',
    'image/heif',
  ].includes(contentType)
    || contentType.startsWith('video/')
    || contentType === 'application/pdf'
    || contentType.startsWith('audio/')
    || contentType === 'text/plain'
    || contentType === 'text/csv'
    || contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    || contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
}

export async function createTaskWithAttachmentsViaServer(
  user: User,
  taskId: string,
  input: {
    title: string;
    description: string;
    assigneeUids: string[];
    studentIds: string[];
    dueAt: string | null;
    recurrence: Recurrence;
    kind: TaskKind;
    timeOptions: TaskTimeOption[];
    source: TaskDoc['source'];
    attachmentItems: Array<
      | { type: 'reservation'; reservationId: string; label: string }
      | { type: 'link'; url: string; label: string }
    >;
    choiceOptions?: TaskChoiceOption[];
    showOn?: string | null;
    reminderDaysBefore?: number[];
    timedAlerts?: true;
  }
): Promise<{ scheduled: boolean }> {
  const response = await fetch('/.netlify/functions/taskWrite', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: await functionHeaders(user),
    body: JSON.stringify({ taskId, input }),
  });
  const payload = await response.json().catch(() => ({})) as { error?: string; scheduled?: unknown };
  if (!response.ok || payload.error) {
    const messages: Record<string, string> = {
      ...TASK_SCHEDULE_ERROR_MESSAGES,
      not_allowed: 'You no longer have access to create this to-do.',
      assign_not_allowed:
        'Staff can assign a to-do from a message to someone else; family accounts can only make their own. '
        + 'You can still make it a to-do for yourself.',
      attachment_not_ready: 'One uploaded to-do attachment is no longer ready. Choose it again.',
      attachment_already_used: 'One uploaded to-do attachment was already used. Choose it again.',
      conflict: 'That to-do changed in another tab. Refresh before trying again.',
      source_not_found: 'The message or post connected to this to-do is no longer available.',
      too_many_alerts: 'You already have 10 reminders with alerts for that day, the most one account can set.',
    };
    throw new Error(messages[payload.error ?? ''] ?? 'The to-do could not be saved.');
  }
  return { scheduled: payload.scheduled === true };
}

/** Plain words for the server's Show on and reminder refusals (packet CX-2). */
const TASK_SCHEDULE_ERROR_MESSAGES: Record<string, string> = {
  show_on_not_future: 'Pick a Show on date after today, or leave it blank to show it now.',
  show_on_after_due: 'Pick a Show on date on or before the due date.',
  reminder_day_not_allowed: 'One of the reminders you picked can no longer be sent. Check the reminders and try again.',
  reminder_needs_due_date: 'Pick a due date to add reminders.',
  schedule_needs_one_time: 'A Show on date and reminders work on one-time to-dos. Set Repeat to One-time to use them.',
  not_waiting: 'This to-do has already shown, so it cannot be changed here.',
  not_found: 'That to-do is no longer there.',
};

async function scheduledTaskRequest(user: User, body: Record<string, unknown>): Promise<void> {
  const response = await fetch('/.netlify/functions/taskWrite', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: await functionHeaders(user),
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok || payload.error) {
    const messages: Record<string, string> = {
      ...TASK_SCHEDULE_ERROR_MESSAGES,
      not_allowed: 'Only the person who made this to-do or a Director can change it.',
      source_not_found: 'The message or post this to-do came from is no longer open to you, so it cannot be changed here.',
    };
    throw new Error(messages[payload.error ?? ''] ?? 'That change could not be saved.');
  }
}

/**
 * Change a waiting to-do whole: its title, its words, who it is for, its Show
 * on day and its reminders (DECISIONS 110 question 4, "Also edit words and
 * people"). Its maker or a Director, only before it shows; the server checks
 * both. (The server's older updateTaskSchedule action, Show on day and
 * reminders only, stays for a tab opened before this shipped.) Nobody has been told about it yet, so a person taken off
 * has nothing to take back, and a person added is told on the Show on morning
 * exactly like everyone picked when it was made (lib/taskWrite.mjs
 * updateWaitingTask).
 */
export async function updateWaitingTask(
  user: User,
  taskId: string,
  change: {
    title: string;
    description: string;
    assigneeUids: string[];
    studentIds: string[];
    showOn: string;
    reminderDaysBefore: number[];
  }
): Promise<void> {
  await scheduledTaskRequest(user, {
    action: 'updateWaitingTask',
    taskId,
    input: {
      title: change.title.trim(),
      description: change.description.trim(),
      assigneeUids: Array.from(new Set(change.assigneeUids)),
      studentIds: Array.from(new Set(change.studentIds)),
      showOn: change.showOn,
      reminderDaysBefore: normalizeReminderDays(change.reminderDaysBefore),
    },
  });
}

/** Cancel a waiting to-do before anyone has seen it (packet CX-2). */
export async function cancelScheduledTask(user: User, taskId: string): Promise<void> {
  await scheduledTaskRequest(user, { action: 'cancelScheduledTask', taskId });
}

async function saveAttachedTaskResponseViaServer(
  user: User,
  taskId: string,
  kind: TaskResponseKind,
  response: {
    url: string;
    label: string;
    note?: string;
    selectedOptionIds?: string[];
    attachments: TaskResponseAttachment[];
    complete: boolean;
  }
): Promise<{ attachments: TaskResponseAttachment[] }> {
  const result = await fetch('/.netlify/functions/taskResponseWrite', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: await functionHeaders(user),
    body: JSON.stringify({
      taskId,
      input: {
        kind,
        label: response.label,
        url: response.url,
        note: response.note ?? '',
        selectedOptionIds: response.selectedOptionIds ?? [],
        attachmentReservationIds: response.attachments.map((attachment) => attachment.id),
        complete: response.complete,
      },
    }),
  });
  const payload = await result.json().catch(() => ({})) as {
    error?: string;
    attachments?: unknown;
  };
  if (!result.ok || payload.error || !Array.isArray(payload.attachments)) {
    const messages: Record<string, string> = {
      not_allowed: 'You no longer have access to respond to this to-do.',
      attachment_not_ready: 'One uploaded file is no longer ready. Choose it again and retry.',
      attachment_already_used: 'One uploaded file was already submitted somewhere else. Choose it again.',
      conflict: 'This to-do response changed in another tab. Refresh before trying again.',
    };
    throw new Error(messages[payload.error ?? ''] ?? 'The to-do response could not be saved.');
  }
  return { attachments: payload.attachments as TaskResponseAttachment[] };
}

/**
 * Save a note and optional supporting photos/files on an ordinary task
 * without independently completing it. Room-request declines use this first,
 * then the existing transactional decline path closes the task and notifies
 * the requester. Keeping the response write separate means a failed decline
 * never pretends the request was closed.
 */
export function useSaveTaskNoteResponse() {
  const { user } = useAuth();
  return async (
    taskId: string,
    note: string,
    attachments: TaskResponseAttachment[] = []
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const trimmed = note.trim();
    if (!trimmed) throw new Error('Add a note first.');
    const normalizedAttachments = normalizeTaskResponseAttachments(attachments);
    if (attachments.length !== (normalizedAttachments?.length ?? 0)) {
      throw new Error('One response attachment was not finalized for this to-do. Remove it and try again.');
    }
    if (normalizedAttachments?.length) {
      await saveAttachedTaskResponseViaServer(user, taskId, 'general', {
        url: '',
        label: 'Supporting note',
        note: trimmed.slice(0, 1000),
        attachments: normalizedAttachments,
        complete: false,
      });
      return;
    }
    const batch = writeBatch(db);
    batch.set(doc(db, 'tasks', taskId, 'responses', user.uid), {
      taskId,
      uid: user.uid,
      kind: 'general',
      url: '',
      label: 'Supporting note',
      note: trimmed.slice(0, 1000),
      ...(normalizedAttachments?.length ? { attachments: normalizedAttachments } : {}),
      savedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await batch.commit();
  };
}

/**
 * Exported so any other upload flow that hands a file to useUploadTaskFile()
 * can run the exact same guess client-side first (e.g. to reject a non-audio
 * file with a friendly message before ever calling Storage), instead of
 * re-deriving its own extension table that could quietly drift from this one.
 */
export function inferContentType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_MIME[ext] || 'application/octet-stream';
}

/**
 * Upload a photo, video, or safe file the task's AUTHOR is attaching, and hand back the attachment
 * to put in the composer's list.
 *
 * Runs before the task doc exists, which is why the caller mints the id first.
 * Nothing is saved to Firestore here: if the author changes their mind and
 * never saves the task, the only thing left behind is an orphan file in a
 * folder no task points at, which is a far better failure than a half-written
 * task.
 */
export function useUploadTaskAttachment() {
  const { user } = useAuth();
  return async (
    taskId: string,
    file: File,
    // The composer draft's stable id. Forwarded to the gateway so a retry
    // re-enters the same reservation rather than charging the byte quota for
    // the same file twice.
    requestId?: string,
  ): Promise<TaskAttachment> => {
    if (!user) throw new Error('Sign in required.');
    const inferredType = inferContentType(file);
    if (!isSafeTaskAttachmentContentType(inferredType)) {
      throw new Error('Choose a photo, video, PDF, music file, or Word, Excel, or CSV document.');
    }
    const isImage = inferredType.startsWith('image/');
    const isVideo = inferredType.startsWith('video/');
    const isFile = !isImage && !isVideo;
    if (isVideo && file.size >= TASK_VIDEO_MAX_BYTES) {
      throw new Error('Videos need to be under 6 GB. For anything longer, share a Drive or YouTube link.');
    }
    if (isFile && file.size >= TASK_FILE_MAX_BYTES) {
      throw new Error('Files need to be under 25 MB.');
    }

    let body: Blob = file;
    let contentType = inferredType;
    if (isImage && inferredType !== 'image/gif') {
      body = await downscaleImage(file, inferredType);
      if (body !== file) contentType = 'image/jpeg';
    }
    if (/^image\/hei[cf]$/.test(contentType)) {
      throw new Error(
        "That photo is in Apple's HEIC format, which this browser cannot convert. Export it as a JPEG or PNG and try again."
      );
    }
    if (isImage && body.size >= TASK_IMAGE_MAX_BYTES) {
      throw new Error(
        inferredType === 'image/gif'
          ? 'GIFs need to be under 8 MB.'
          : 'That photo is too large even after shrinking. Pick one under 8 MB.'
      );
    }
    // Author attachments always use the same server control plane as chat.
    // This enforces App Check, live role and task ownership, byte signatures,
    // immutable object creation, and per-account upload quotas before the task
    // can reference the returned URL.
    const { uploadAttachmentViaGateway } = await loadAttachmentGateway();
    const uploaded = await uploadAttachmentViaGateway({
      target: { kind: 'task', taskId },
      source: { blob: body, name: file.name || 'attachment', contentType },
      user,
      requestId,
    });
    return {
      url: uploaded.url,
      kind: uploaded.kind,
      label: uploaded.name,
      reservationId: uploaded.reservationId,
      contentType: uploaded.contentType,
      storagePath: uploaded.storagePath,
    };
  };
}

/**
 * Take an uploaded photo back off a task being composed, object and all.
 *
 * Dropping it from the composer's list is not enough. The file stays in
 * Storage and stays readable to anyone assigned to that task, and the photo
 * somebody removes is the likeliest one to have shown something it should not
 * have. Deleting is best effort on purpose: an object that is already gone, or
 * a link that was pasted rather than uploaded, must not stop the row
 * disappearing from the form.
 */
export async function deleteTaskAttachmentFile(attachment: TaskAttachment): Promise<void> {
  if (!storage) return;
  if (attachment.kind === 'link') return;
  try {
    await deleteObject(storageRef(storage, attachment.storagePath || attachment.url));
  } catch {
    // Already deleted, or not one of ours. Either way the caller drops it.
  }
}

/** Upload a task deliverable through the secure gateway, then atomically save
 * the response, complete the caller's slot, and consume the exact reservation
 * on the server. */
export function useUploadTaskFile() {
  const { user } = useAuth();
  return async (
    taskId: string,
    file: File,
    label: string,
    kind: TaskResponseKind = 'upload-file',
    /** 0..1 upload progress (task #88 - music files show live progress too,
     * same as the class-video path). Optional; omitting it is identical to
     * the pre-existing behavior. */
    onProgress?: (fraction: number) => void,
    /** Legacy recovery identity. It is now used only as a stable gateway
     * request id, never as a client-writable Storage path. */
    storagePathOverride?: string
  ): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const contentType = inferContentType(file);
    if (!isSafeTaskAttachmentContentType(contentType)) {
      throw new Error('Choose a photo, video, PDF, music file, or Word, Excel, or CSV document.');
    }
    const fileKind = contentType.startsWith('image/')
      ? 'image'
      : contentType.startsWith('video/')
        ? 'video'
        : 'file';
    const maxBytes = fileKind === 'image'
      ? TASK_IMAGE_MAX_BYTES
      : fileKind === 'video'
        ? TASK_VIDEO_MAX_BYTES
        : TASK_FILE_MAX_BYTES;
    if (file.size >= maxBytes) {
      throw new Error(
        fileKind === 'video'
          ? 'Videos need to be under 6 GB. For anything longer, share a Drive or YouTube link.'
          : fileKind === 'image'
            ? 'Photos need to be under 8 MB.'
            : 'Files need to be under 25 MB.'
      );
    }
    const gatewayRequestId = storagePathOverride
      ? await stableTaskResponseRequestId(storagePathOverride)
      : undefined;
    const { uploadAttachmentViaGateway } = await loadAttachmentGateway();
    const uploaded = await uploadAttachmentViaGateway({
      target: { kind: 'taskResponse', taskId },
      source: { blob: file, name: file.name || 'attachment', contentType },
      user,
      requestId: gatewayRequestId,
      onProgress,
    });
    const saved = await saveAttachedTaskResponseViaServer(user, taskId, kind, {
      url: '',
      label: label.trim() || file.name || 'Uploaded file',
      attachments: [{
        id: uploaded.id,
        kind: uploaded.kind,
        url: uploaded.url,
        name: uploaded.name,
        contentType: uploaded.contentType,
        storagePath: uploaded.storagePath,
      }],
      complete: true,
    });
    await finishTaskNotificationCleanup(taskId);
    return saved.attachments[0].url;
  };
}

async function stableTaskResponseRequestId(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  if (globalThis.crypto?.subtle) {
    const digest = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', bytes));
    return Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('').slice(0, 64);
  }
  return value.replace(/[^A-Za-z0-9_-]/g, '_').slice(-100).padStart(12, '0');
}

/** Stable path a class-media reservation can persist before the upload. */
export function taskUploadStoragePath(
  taskId: string,
  uid: string,
  fileName: string,
  uploadId = `${Date.now()}`
): string {
  const safeName = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  return `task-response-recovery/${taskId}/${uid}/${uploadId}-${safeName}`;
}

async function finishTaskNotificationCleanup(taskId: string): Promise<void> {
  try {
    await clearNotificationsForTask(taskId);
  } catch (cause) {
    // The task write happens first. Name that committed state so a cleanup
    // problem never tells someone their completed work was lost.
    throw new Error(
      'This to-do is complete, but its old notification could not be cleared. Open Notifications and tap Dismiss if it still appears.',
      { cause }
    );
  }
}

/**
 * Per-assignee completion. Writes completedBy.{myUid} = serverTimestamp.
 * Rule allows this for any uid in assigneeUids, but only their own slot.
 *
 * If this completion fills the LAST assignee slot AND the task is
 * recurring AND nextInstanceId is null, we spawn the next instance in
 * the same transaction. The transaction's read-then-write protects
 * against two assignees both racing to spawn — only one will see the
 * "I'm the last + no successor yet" state and the other will see a
 * stale read + retry, finding nextInstanceId already set.
 */
export function useCompleteTask() {
  const { user } = useAuth();
  const { userDoc, realUserDoc } = useUserDoc();
  // Spawning the next recurring instance is a task CREATE, which rules only
  // allow for active staff (with createdBy == the caller). A family assignee
  // finishing the last slot must therefore complete WITHOUT spawning, or the
  // rules reject their whole transaction and they can never mark it done.
  const canSpawn =
    !!userDoc && userDoc.status === 'active' && userDoc.role !== 'family';
  return useCallback(async (
    taskId: string,
    /**
     * Who the to-do names, when the caller has it. A Jr Staff ticking a to-do
     * given to her OTHER sign-in (refute round on B2, finding 6) goes through
     * the jrStaffReads lane: the rules let only that sign-in tick its slot,
     * and the to-do reached this one from the lane in the first place.
     */
    options: { assigneeUids?: readonly string[] } = {}
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    // The REAL role (final review on B2): a Director previewing Jr Staff
    // ticks as herself, never through the Jr Staff lane.
    if (
      realUserDoc?.role === 'ensemble'
      && realUserDoc.status === 'active'
      && options.assigneeUids
      && options.assigneeUids.length > 0
      && !options.assigneeUids.includes(user.uid)
    ) {
      await completeJrStaffAliasTask(taskId);
      refreshJrStaffOtherSignIn();
      await finishTaskNotificationCleanup(taskId);
      return;
    }
    const firestore = db;
    const taskRef = doc(firestore, 'tasks', taskId);

    // Fast path: a spawn is only ever possible when the task is recurring,
    // hasn't already spawned its successor, and I'm someone who's allowed to
    // spawn. That's a small minority of completions (most tasks are one-time
    // 'general'/upload/payment tasks). runTransaction always pays for a real
    // server read + commit round trip before the UI can update; a plain
    // updateDoc gets Firestore's local-write echo instead, so the "done"
    // state shows up the instant the write is queued, not after the network
    // round trip - that's the actual fix for "slow to check off." Peek at the
    // already-cached task doc (every place this hook is called from also has
    // an active tasks/{} listener open, so the doc is normally already in the
    // local cache) to see whether a spawn is even on the table before paying
    // for a transaction. If the cache read misses for any reason, fall back
    // to the always-correct transaction path below exactly as before -
    // this peek is a pure optimization and never changes what gets written.
    let cached: TaskDoc | null = null;
    try {
      const cachedSnap = await getDocFromCache(taskRef);
      if (cachedSnap.exists()) {
        cached = normalizeTaskDoc(cachedSnap.id, cachedSnap.data());
      }
    } catch {
      cached = null;
    }
    // A retry after notification cleanup failed must not rewrite the saved
    // completion time. It only retries the unfinished cleanup step.
    if (cached && user.uid in cached.completedBy) {
      await finishTaskNotificationCleanup(taskId);
      return;
    }
    const spawnImpossible =
      !!cached &&
      (cached.recurrence === 'none' || !canSpawn || cached.nextInstanceId !== null);

    if (spawnImpossible) {
      await updateDoc(taskRef, {
        [`completedBy.${user.uid}`]: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await finishTaskNotificationCleanup(taskId);
      return;
    }

    await runTransaction(firestore, async (tx) => {
      const snap = await tx.get(taskRef);
      if (!snap.exists()) throw new Error('To-do disappeared.');
      const data = normalizeTaskDoc(snap.id, snap.data());
      // Same retry guard as the cache fast path. A completed recurring task
      // must never spawn another successor just because card cleanup retried.
      if (user.uid in data.completedBy) return;

      // Compute the post-completion completedBy map locally so we can
      // tell if I'm the final acker.
      const nextCompletedBy = {
        ...data.completedBy,
        [user.uid]: FirestoreTimestamp.now(),
      };
      const willBeDone = data.assigneeUids.every(
        (uid) => uid in nextCompletedBy
      );
      const shouldSpawn =
        willBeDone &&
        canSpawn &&
        data.recurrence !== 'none' &&
        data.nextInstanceId === null &&
        !!data.dueAt;

      if (shouldSpawn) {
        const prevDue = (data.dueAt as Timestamp).toDate();
        const nextDue = computeNextDueAt(prevDue, data.recurrence);
        const newRef = doc(collection(firestore, 'tasks'));
        tx.set(newRef, {
          title: data.title,
          description: data.description,
          assigneeUids: data.assigneeUids,
          studentIds: data.studentIds ?? [],
          dueAt: nextDue,
          completedBy: {},
          recurrence: data.recurrence,
          nextInstanceId: null,
          kind: data.kind ?? 'general',
          uploadLinks: {},
          source: null,
          createdAt: serverTimestamp(),
          // The COMPLETER authors the spawned instance. Rules require
          // createdBy == request.auth.uid on create, so copying the original
          // creator's uid (the old behavior) made rules reject the whole
          // completion whenever the final acker wasn't the creator.
          createdBy: user.uid,
          createdByName: userDoc
            ? teacherDisplayName(userDoc)
            : user.displayName || null,
          updatedAt: serverTimestamp(),
        });
        tx.update(taskRef, {
          [`completedBy.${user.uid}`]: serverTimestamp(),
          nextInstanceId: newRef.id,
          updatedAt: serverTimestamp(),
        });
      } else {
        tx.update(taskRef, {
          [`completedBy.${user.uid}`]: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    });
    await finishTaskNotificationCleanup(taskId);
  }, [user, userDoc, realUserDoc, canSpawn]);
}

/**
 * Director-only delete.
 */
export async function deleteTask(id: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await deleteDoc(doc(db, 'tasks', id));
}

/**
 * "Remind me later" for the signed-in assignee. Writes only their own key
 * inside snoozedUntil{}, so the task quietens for them and stays exactly as
 * it was for everyone else. Pass null to un-snooze (the "Bring it back" undo).
 * Nothing is marked done; the task simply stops nagging until the chosen time.
 */
export async function snoozeTaskForMe(
  taskId: string,
  uid: string,
  until: Date | null
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'tasks', taskId), {
    // Firestore converts a JS Date to a Timestamp on write.
    [`snoozedUntil.${uid}`]: until ?? deleteField(),
    updatedAt: serverTimestamp(),
  });
}

/** Turn a snooze choice into a concrete moment to resurface the task. */
export function snoozeUntilFrom(choice: SnoozeChoiceKey, now: Date = new Date()): Date {
  const found = SNOOZE_CHOICES.find((c) => c.key === choice) ?? SNOOZE_CHOICES[1];
  return new Date(now.getTime() + found.hours * 60 * 60 * 1000);
}

/**
 * Mark (or unmark) one assignee's completedBy slot on a kind:'payment-needed'
 * task. This is the front-desk workflow: a family pays cash in person, and
 * an Admin/FOH account checks it off (or undoes a mistaken mark) without
 * needing the family's own account signed in. Rules scope this narrowly to
 * payment tasks and to the completedBy map only - Director and the family
 * assignee keep their existing paths untouched.
 */
export async function markAssigneePaid(
  taskId: string,
  assigneeUid: string,
  paid: boolean
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'tasks', taskId), {
    [`completedBy.${assigneeUid}`]: paid ? serverTimestamp() : deleteField(),
    updatedAt: serverTimestamp(),
  });
}

// ---------------------------------------------------------------------------
// Display helpers shared by /tasks and the family dancer profile's task and
// class-message cards. Kept small and generic (they work on any
// Timestamp-keyed map, not just TaskDoc) so both surfaces can show the same
// "Posted by ... on ..." / "Marked done by ... on ..." bylines without
// duplicating the timestamp math.
// ---------------------------------------------------------------------------

/**
 * Plain short date like "Jul 12". Returns '' when the value isn't a real
 * Timestamp yet (pending serverTimestamp write, missing field, etc).
 */
/**
 * "Jul 26 at 4:12 PM" - date AND time.
 *
 * A completion stamp answers "who signed off, and when". The date alone
 * leaves a whole day of ambiguity, which is no help when the question is
 * whether something was done before or after a class.
 */
export function formatDateAndTime(value: Timestamp | FieldValue | null | undefined): string {
  if (!value || typeof (value as Timestamp).toDate !== 'function') return '';
  const d = (value as Timestamp).toDate();
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date} at ${time}`;
}

export function formatShortDate(value: Timestamp | FieldValue | null | undefined): string {
  if (!value || typeof (value as Timestamp).toDate !== 'function') return '';
  return (value as Timestamp).toDate().toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Given a uid -> Timestamp map (completedBy, acks, dismissedBy, ...), find
 * the entry with the latest timestamp. Used to answer "who finished this
 * last" for the "Marked done by ..." line.
 */
export function latestTimestampEntry(
  map: Record<string, Timestamp | FieldValue> | null | undefined
): { uid: string; at: Timestamp } | null {
  if (!map) return null;
  let best: { uid: string; at: Timestamp } | null = null;
  for (const [uid, value] of Object.entries(map)) {
    if (!value || typeof (value as Timestamp).toMillis !== 'function') continue;
    const at = value as Timestamp;
    if (!best || at.toMillis() > best.at.toMillis()) best = { uid, at };
  }
  return best;
}

/**
 * Resolve a uid to a friendly display name without ever falling back to a
 * raw uid. "You" when it's the viewer; otherwise whatever `lookupName`
 * finds (e.g. a staff directory map); otherwise a generic, still-readable
 * fallback phrase.
 */
export function resolvePersonLabel(
  uid: string,
  meUid: string | null | undefined,
  lookupName?: (uid: string) => string | null | undefined,
  fallback = 'a studio account'
): string {
  if (meUid && uid === meUid) return 'You';
  const looked = lookupName ? lookupName(uid) : null;
  if (looked && looked.trim()) return looked.trim();
  return fallback;
}

/**
 * THE PERSON WHO MADE A TO-DO, whatever its card says: "You" to her, then the
 * denormalized createdByName captured at creation time (works even when the
 * viewer has no read access to the creator's users/{uid} doc, e.g. a family
 * account), then a directory lookup, then a generic phrase. Never prints a
 * raw uid.
 *
 * Every line that names who an action REACHES reads this, never
 * taskCreatorLabel: the To-dos card's Ask button, its "Sends a message to"
 * line and its placeholder (Tasks.tsx), because Ask opens a private message
 * with t.createdBy. Telling a family a question goes to The Studio LDG when
 * it lands with one teacher would be untrue (refute round on #1352), and the
 * brief for DECISIONS 123 item 3 kept routing as it is.
 */
export function taskMakerLabel(
  t: Pick<TaskDoc, 'createdBy' | 'createdByName'>,
  meUid: string | null | undefined,
  lookupName?: (uid: string) => string | null | undefined
): string {
  if (meUid && t.createdBy && t.createdBy === meUid) return 'You';
  const denorm = t.createdByName?.trim();
  if (denorm) return denorm;
  return resolvePersonLabel(t.createdBy, meUid, lookupName, 'the studio team');
}

/**
 * "Posted by" label for a task's creator: the person who made it
 * (taskMakerLabel), except that a to-do made with a Show on day or reminders
 * says The Studio LDG instead, to everyone, its maker included (Bobbie,
 * DECISIONS 123 item 3: "The Studio LDG everywhere"; isStudioVoiceTask in
 * lib/taskSchedule.ts). This one label is the to-do card's "Posted by" line
 * on the To-dos page, the Messages to-do row and the family dancer profile,
 * so they all change together. The Ask button is NOT one of them: it names
 * the person the message reaches (taskMakerLabel).
 */
export function taskCreatorLabel(
  t: Pick<TaskDoc, 'createdBy' | 'createdByName'>
    & Partial<Pick<TaskDoc, 'showOn' | 'shownOn' | 'showOnAssigneeUids' | 'reminderDaysBefore'>>
    & Partial<Pick<TaskDoc, 'id' | 'bagGroupId' | 'bagItemId'>>,
  meUid: string | null | undefined,
  lookupName?: (uid: string) => string | null | undefined
): string {
  if (isStudioVoiceTask(t)) return TASK_STUDIO_NAME;
  // A bag item to-do is posted by its bag group, whoever's tap wrote it: the
  // parent who claimed it never "posted" a to-do (BG-8 refute round 1). The
  // item lane stores the group's name as createdByName.
  if (typeof t.id === 'string' && bagItemTaskGroupId({ id: t.id, bagGroupId: t.bagGroupId, bagItemId: t.bagItemId })) {
    return t.createdByName?.trim() || 'Bag group';
  }
  return taskMakerLabel(t, meUid, lookupName);
}

/**
 * The names the To-dos search finds a to-do by, for who made it: what its
 * card says (The Studio LDG on a Show on or reminder to-do) AND the person
 * who made it, so typing a teacher's name still finds the to-dos she made
 * (refute round on #1352). Her name is already on that card's Ask button, so
 * the search shows nobody anything the card does not.
 */
export function taskCreatorSearchNames(
  t: Pick<TaskDoc, 'createdBy' | 'createdByName'>
    & Partial<Pick<TaskDoc, 'showOn' | 'shownOn' | 'showOnAssigneeUids' | 'reminderDaysBefore'>>
    & Partial<Pick<TaskDoc, 'id' | 'bagGroupId' | 'bagItemId'>>,
  meUid: string | null | undefined,
  lookupName?: (uid: string) => string | null | undefined
): string[] {
  const shown = taskCreatorLabel(t, meUid, lookupName);
  const maker = taskMakerLabel(t, meUid, lookupName);
  return shown === maker ? [shown] : [shown, maker];
}

/**
 * Badge/pill label for a task kind, suppressed for 'timed-reminder'. A
 * timed reminder's whole point is its own title/content ("Bring your
 * costume Friday"), so restating the kind as a pill next to it just
 * repeats "Timed reminder" as noise. Every other kind's pill stays because
 * it tells the reader what action is expected (Upload music, Costume
 * pickup, ...). TASK_KIND_LABELS itself is untouched, since composer
 * dropdowns (NewTask, Command Center) still need the full label to let
 * staff pick a timed reminder in the first place.
 */
export function taskKindBadgeLabel(kind: TaskKind): string | null {
  if (kind === 'timed-reminder') return null;
  return TASK_KIND_LABELS[kind];
}

// ---------------------------------------------------------------------------
// Production Tracker's request/reminder to-dos (kind:'general' - see
// pages/ProductionTracker.tsx sendRequestTasks, sendTeacherReminder,
// sendTeacherBulkReminder, markUnavailable) describe a missing music or
// costume deliverable that actually lives on the OFFERING
// (offerings.musicLinks / musicReviewStatus, or a linked costume record),
// not on the task itself - the task is a nudge pointing at Production
// Tracker, never the deliverable. Tapping "Mark done" on one of these only
// dismisses the nudge; it proves nothing about whether the music or costume
// actually landed, which is exactly the "green check over nothing" pattern
// this file's completion model is built to avoid for real upload tasks. All
// four of those call sites write the same "Open Production Tracker" / "class
// workspace" phrasing into the description - that is the fingerprint used
// here to recognize them and point the person at the right place instead of
// a bare, disconnected Mark done.
// ---------------------------------------------------------------------------

const PRODUCTION_TRACKER_FINGERPRINT =
  /open (?:production tracker|comp\/recital tracker|recital\/comp (?:due dates|tracker))|class workspace/i;

/** True for a task that is really a nudge toward Production Tracker. */
export function isProductionTrackerReminderTask(t: Pick<TaskDoc, 'description'>): boolean {
  return PRODUCTION_TRACKER_FINGERPRINT.test(t.description ?? '');
}

const SINGLE_CLASS_REMINDER_PREFIX = 'Music and costume reminder for ';

/**
 * The exact offering name for a single-class reminder - ProductionTracker's
 * sendTeacherReminder writes this exact title prefix, one offering per task.
 * Multi-class reminders (sendTeacherBulkReminder) and bulk requests
 * (sendRequestTasks) list several classes in the description and are
 * intentionally NOT matched here: there is no safe way to tell which of
 * several listed classes a single shared task's completion refers to, so
 * guessing would risk auto-completing a task while real work for a
 * DIFFERENT listed class is still outstanding.
 */
export function productionTrackerReminderOfferingName(
  t: Pick<TaskDoc, 'title' | 'description'>
): string | null {
  if (!isProductionTrackerReminderTask(t)) return null;
  if (!t.title.startsWith(SINGLE_CLASS_REMINDER_PREFIX)) return null;
  const name = t.title.slice(SINGLE_CLASS_REMINDER_PREFIX.length).trim();
  return name || null;
}

// Anchored to line start AND end-of-line so a class id can only come from
// the writer's own trailing "Class id X." line, never from id-shaped words
// inside reminder prose someone typed by hand.
const REMINDER_CLASS_ID_LINE = /^Class id ([A-Za-z0-9_-]+)\.?\s*$/m;

/**
 * The exact offering ID for a single-class reminder, from the trailing
 * "Class id X." line sendTeacherReminder writes. Class NAMES are not unique
 * (two Ballet 1s are real at this studio), and closing a reminder off a
 * name match once completed the task against the WRONG same-named class
 * while the intended one still had no music - the id is the only anchor
 * that cannot cross wires. Null for legacy reminders written before the id
 * line existed; the auto-closer then falls back to the name, but only when
 * exactly one offering carries it.
 */
export function productionTrackerReminderOfferingId(
  t: Pick<TaskDoc, 'title' | 'description'>
): string | null {
  if (!isProductionTrackerReminderTask(t)) return null;
  if (!t.title.startsWith(SINGLE_CLASS_REMINDER_PREFIX)) return null;
  const match = REMINDER_CLASS_ID_LINE.exec(t.description ?? '');
  return match ? match[1] : null;
}

/**
 * My open tasks, sorted "what should I do next first":
 *   - overdue (due in the past + not completed by me) → top
 *   - then due-soonest
 *   - then no-due-date tasks
 *
 * Useful for the home screen surface where Bobbie wants a top-3
 * "what needs your attention" glance without opening /tasks.
 */
export interface UseMyOpenTasksResult {
  loading: boolean;
  tasks: TaskDoc[];
  /**
   * See UseTasksResult.confirmed. This rides useMyAssignedTasks' listener, so
   * it carries the same "loading:false is not proof this count is current"
   * caveat - a consumer showing a bare number (the home "Open to-dos for you"
   * badge) should prefer this over trusting `!loading` alone.
   */
  confirmed: boolean;
  /**
   * The listener's error, if it has one. Returned because `confirmed` cannot
   * carry this fact: useMyAssignedTasks' error callback sets `error` and
   * clears `loading` but never sets `confirmed`, so a DENIED read and a read
   * that is merely still catching up are the same `confirmed:false` from
   * out here. A consumer that turns confirmed:false into "still checking..."
   * says that forever on a dead listener. Added 2026-09-05 for the family
   * ask chat, which did exactly that on the "What's due?" chip.
   */
  error: string | null;
}

/**
 * `enabled` (perf round, home-listener-dedupe) passes straight through to
 * useMyAssignedTasks - see its doc comment. Defaults true, so every
 * existing caller (HeroAndTasks' HomeTaskSummary, useFamilyAskContext, the
 * pre-lift call sites) keeps its exact subscription.
 */
export function useMyOpenTasks(maxCount = 3, enabled = true): UseMyOpenTasksResult {
  const { user } = useAuth();
  // Bounded read (perf round): the home summary and tile badges only ever
  // show MY tasks, so they ride the assignee-scoped capped query instead
  // of syncing the whole studio task board. The assigneeUids filter below
  // is now redundant for the query result but kept as belt-and-braces.
  const { tasks, loading, confirmed, error } = useMyAssignedTasks(150, enabled);
  const sorted = (() => {
    if (!user) return [];
    // isTaskOpenForMe is THE shared "still open for me" predicate - see its
    // definition. It deliberately does NOT exclude a snoozed task: the
    // /tasks page keeps a snoozed card in the open section too (with a
    // "Reminding you..." row of its own), so this count has to agree with
    // what that list actually shows, not with a narrower "needs urgent
    // attention right now" reading that would make the two disagree again.
    const mine = tasks.filter((t) => isTaskOpenForMe(t, user.uid));
    mine.sort((a, b) => {
      const aMs =
        a.dueAt && typeof (a.dueAt as Timestamp).toMillis === 'function'
          ? (a.dueAt as Timestamp).toMillis()
          : Number.POSITIVE_INFINITY;
      const bMs =
        b.dueAt && typeof (b.dueAt as Timestamp).toMillis === 'function'
          ? (b.dueAt as Timestamp).toMillis()
          : Number.POSITIVE_INFINITY;
      return aMs - bMs;
    });
    return mine.slice(0, maxCount);
  })();
  return { loading, tasks: sorted, confirmed, error };
}

/**
 * FAMILY TO-DOS (N20). Bobbie, 2026-08-13, her word "final": "parents can
 * only create for themselves and their family". A parent makes, changes,
 * marks done and removes to-dos on herself, the other parent and her own
 * dancers, through the server (netlify/functions/lib/familyTodoWrite.mjs),
 * which reads her family fresh and refuses anyone outside it and any to-do
 * the studio made. A family to-do sends no alert.
 */
export interface FamilyTodoMember {
  uid: string;
  name: string | null;
  /** What the card prints: the name, plus "(dancer app)" or "(staff account)" for a Jr Staff sign-in on its own. */
  label: string;
  me: boolean;
  personUid: string;
}
export interface FamilyTodoDancer { studentId: string; name: string }
export interface FamilyTodoRow {
  id: string;
  title: string;
  description: string;
  assigneeUids: string[];
  studentIds: string[];
  dueAtMs: number | null;
  doneUids: string[];
  createdBy: string;
  createdByName: string | null;
  createdAtMs: number | null;
  /**
   * False on a to-do she made that she can no longer manage (the studio
   * unlinked someone on it): she may remove it and nothing else.
   */
  canManage: boolean;
}
/** The server's caps on a family to-do (lib/familyTodoWrite.mjs). */
export const FAMILY_TODO_TITLE_MAX = 200;
export const FAMILY_TODO_NOTES_MAX = 2000;
export interface FamilyTodoInput {
  title: string;
  description: string;
  assigneeUids: string[];
  studentIds: string[];
  dueAt: string | null;
}

const FAMILY_TODO_ERROR_MESSAGES: Record<string, string> = {
  not_allowed: 'Only to-dos your family made can be changed here.',
  assign_not_allowed: 'Family to-dos can go on you, your family and your own dancers only.',
  not_found: 'That to-do is no longer there.',
  conflict: 'That to-do changed in another tab. Refresh before trying again.',
  too_many_family_todos: 'You have added 30 family to-dos today, the most for one day. You can add more tomorrow.',
  bad_request: `Check the to-do: a title up to ${FAMILY_TODO_TITLE_MAX} characters, notes up to ${FAMILY_TODO_NOTES_MAX}, and at least one person.`,
};

async function familyTodoRequest<T>(user: User, body: Record<string, unknown>): Promise<T> {
  const response = await fetch('/.netlify/functions/taskWrite', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: await functionHeaders(user),
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({})) as { error?: string } & T;
  if (!response.ok || payload.error) {
    throw new Error(FAMILY_TODO_ERROR_MESSAGES[payload.error ?? ''] ?? 'That could not be saved. Try again.');
  }
  return payload;
}

export function listFamilyTodos(user: User): Promise<{
  members: FamilyTodoMember[]; dancers: FamilyTodoDancer[]; todos: FamilyTodoRow[]; truncated: boolean;
}> {
  return familyTodoRequest(user, { action: 'listFamilyTodos' });
}

/** The server mints the id (refute round 2), so none is sent. */
export async function createFamilyTodo(user: User, input: FamilyTodoInput): Promise<void> {
  await familyTodoRequest(user, { action: 'createFamilyTodo', input });
}

export async function updateFamilyTodo(user: User, taskId: string, input: FamilyTodoInput): Promise<void> {
  await familyTodoRequest(user, { action: 'updateFamilyTodo', taskId, input });
}

export async function completeFamilyTodo(user: User, taskId: string, done: boolean): Promise<void> {
  await familyTodoRequest(user, { action: 'completeFamilyTodo', taskId, done });
}

export async function deleteFamilyTodo(user: User, taskId: string): Promise<void> {
  await familyTodoRequest(user, { action: 'deleteFamilyTodo', taskId });
}
